import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  User,
  Phone,
  Mail,
  Smartphone,
  ShieldCheck,
  ShieldAlert,
  Wrench,
  Plus,
  Trash2,
  DollarSign,
  Printer,
  Download,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Check,
  FileCheck2,
  Receipt,
  Sparkles,
  Edit3,
  Package,
  Lock,
  History,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Tag,
  Building2,
  Truck,
  Layers,
  AlertCircle,
  UserCheck,
  UserPlus,
  UserX,
  Users,
  FileText,
  Wallet,
  MapPin,
} from 'lucide-react';
import { AppUser, CompanyInfo, InventoryItem, PaymentMethod, PaymentRecord, PaymentStatus, ServicePart, ServiceTicket, TicketStatus } from '../types';
import {
  calculateTicketTotal,
  calculateTicketPartsCost,
  calculateTicketTotalCost,
  getTicketGrandTotal,
  getTicketPaymentSplit,
  formatTicketPaymentMethodDisplay,
  formatPhoneDisplay,
  formatDateTR,
  formatTL,
  formatUSD,
  convertUSDToTL,
  getStoredExchangeRate,
  STATUS_COLORS,
  STATUS_LABELS,
  getStoredUsers,
  isTicketAssignedToUser,
  cleanTechnicianNotes,
  normalizePaymentMethod,
  getTicketContractBreakdown,
  getSortedLotsByLowestCost,
  consumeItemStockLowestCostFirst,
  getLotCostTL,
  getItemActiveLots,
} from '../utils/storage';
import { exportTicketIntakePDF, exportTicketInvoicePDF } from '../utils/pdfGenerator';
import { Calendar, Key, Grid3X3, MessageSquare, Copy, Search, Gauge, TrendingUp, RotateCcw, FileSpreadsheet, Info } from 'lucide-react';
import { PatternLockModal, PatternThumbnail, isPatternPassword } from './PatternLockModal';
import { getCustomerTrackingUrl } from '../utils/smsService';

interface TicketDetailModalProps {
  ticket: ServiceTicket | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedTicket: ServiceTicket) => void;
  onDeleteTicket: (id: string) => void;
  companyInfo: CompanyInfo;
  onPrintIntake: (ticket: ServiceTicket) => void;
  onPrintInvoice: (ticket: ServiceTicket) => void;
  onPrintLabel?: (ticket: ServiceTicket) => void;
  onOpenSms?: (ticket: ServiceTicket) => void;
  allTickets?: ServiceTicket[];
  onSelectTicket?: (ticket: ServiceTicket) => void;
  inventory?: InventoryItem[];
  isTechnician?: boolean;
  currentUser?: AppUser | null;
  deviceTypes?: string[];
}

const STATUS_ORDER: TicketStatus[] = [
  'Kaydedildi',
  'Incelemede',
  'TestEdiliyor',
  'Sivida',
  'MusteriOnayi',
  'Tamirde',
  'ParcaBekliyor',
  'AltServiste',
  'Onaylandi',
  'Onaylanmadi',
  'Hazir',
  'IadeEdilecek',
  'IadeEdildi',
  'TeslimEdildi',
];

function isoToDatetimeLocal(isoStr?: string): string {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => (n < 10 ? '0' + n : String(n));
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
}

function datetimeLocalToIso(dtStr: string, fallback: string): string {
  if (!dtStr) return fallback;
  try {
    const d = new Date(dtStr);
    if (isNaN(d.getTime())) return fallback;
    return d.toISOString();
  } catch {
    return fallback;
  }
}

interface RegisteredSubServiceItem {
  name: string;
  ticketCount: number;
  unpaidDebt: number;
  totalSpent: number;
  lastUsedDate?: string;
  lastUsedCost?: number;
}

export const TicketDetailModal: React.FC<TicketDetailModalProps> = (props) => {
  if (!props.isOpen || !props.ticket) return null;
  return <TicketDetailModalContent key={props.ticket.id} {...props} ticket={props.ticket} />;
};

const TicketDetailModalContent: React.FC<TicketDetailModalProps & { ticket: ServiceTicket }> = ({
  ticket,
  isOpen,
  onClose,
  onSave,
  onDeleteTicket,
  companyInfo,
  onPrintIntake,
  onPrintInvoice,
  onPrintLabel,
  onOpenSms,
  allTickets,
  onSelectTicket,
  inventory = [],
  isTechnician = false,
  currentUser,
  deviceTypes = [],
}) => {
  const [status, setStatus] = useState<TicketStatus>(ticket.status);
  const [performedActions, setPerformedActions] = useState(ticket.performedActions || '');
  const [technicianNotes, setTechnicianNotes] = useState(cleanTechnicianNotes(ticket.technicianNotes));
  const [isUnderWarranty, setIsUnderWarranty] = useState(ticket.isUnderWarranty);
  const [deviceType, setDeviceType] = useState(ticket.deviceType || '');
  const [laborCost, setLaborCost] = useState<number>(ticket.laborCost || 0);
  const [vatRate, setVatRate] = useState<number>(20);
  const [isVatIncluded, setIsVatIncluded] = useState<boolean>(ticket.isVatIncluded || false);
  const [discount, setDiscount] = useState<number>(ticket.discount || 0);
  const [roundingAdjustment, setRoundingAdjustment] = useState<number>(0);
  
  // Split payment breakdown states (Nakit, Kredi Kartı, EFT/Havale)
  const initialSplit = useMemo(() => getTicketPaymentSplit(ticket), [ticket]);
  const [cashAmount, setCashAmount] = useState<number>(initialSplit.cash);
  const [cardAmount, setCardAmount] = useState<number>(initialSplit.card);
  const [transferAmount, setTransferAmount] = useState<number>(initialSplit.transfer);

  const totalPaid = useMemo(() => {
    return (cashAmount || 0) + (cardAmount || 0) + (transferAmount || 0);
  }, [cashAmount, cardAmount, transferAmount]);

  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>(ticket.paymentStatus);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | undefined>(ticket.paymentMethod);
  const [parts, setParts] = useState<ServicePart[]>(ticket.parts || []);
  const [includedAccessories, setIncludedAccessories] = useState<string[]>(ticket.includedAccessories || []);
  const [devicePassword, setDevicePassword] = useState<string>(ticket.devicePassword || '');
  const [isPatternModalOpen, setIsPatternModalOpen] = useState<boolean>(false);
  const [newAccessoryInput, setNewAccessoryInput] = useState('');
  const [editingAccessoryIdx, setEditingAccessoryIdx] = useState<number | null>(null);
  const [editingAccessoryText, setEditingAccessoryText] = useState('');

  const [deliveryDateInput, setDeliveryDateInput] = useState<string>(
    ticket ? isoToDatetimeLocal(ticket.deliveryDate || new Date().toISOString()) : ''
  );
  const [warrantyDays, setWarrantyDays] = useState<number>(
    ticket.warrantyDays !== undefined ? ticket.warrantyDays : 30
  );
  const [savedToast, setSavedToast] = useState<boolean>(false);
  const [isPastHistoryOpen, setIsPastHistoryOpen] = useState<boolean>(false);

  // Assigned Technician state
  const [assignedTechnicianId, setAssignedTechnicianId] = useState<string>(ticket.assignedTechnicianId || '');
  const [assignedTechnicianName, setAssignedTechnicianName] = useState<string>(ticket.assignedTechnicianName || '');
  const [assignedTechnicianUsername, setAssignedTechnicianUsername] = useState<string>(ticket.assignedTechnicianUsername || '');

  // Distinct Personnel Tracking: Kayıt Açan, Onaran, Teslim Eden, Son İşlem Yapan
  const [repairedByName, setRepairedByName] = useState<string>(
    ticket.repairedByName || ticket.assignedTechnicianName || ''
  );
  const [repairedByUser, setRepairedByUser] = useState<string>(
    ticket.repairedByUser || ticket.assignedTechnicianUsername || ''
  );
  const [repairedAt, setRepairedAt] = useState<string>(ticket.repairedAt || '');

  const [deliveredByName, setDeliveredByName] = useState<string>(
    ticket.deliveredByName || (ticket.status === 'TeslimEdildi' || ticket.status === 'IadeEdildi' ? (currentUser?.name || 'Sistem Yöneticisi') : '')
  );
  const [deliveredByUser, setDeliveredByUser] = useState<string>(
    ticket.deliveredByUser || (ticket.status === 'TeslimEdildi' || ticket.status === 'IadeEdildi' ? (currentUser?.username || 'admin') : '')
  );
  const [deliveredAt, setDeliveredAt] = useState<string>(ticket.deliveredAt || ticket.deliveryDate || '');
  const [copiedTrackingLink, setCopiedTrackingLink] = useState(false);

  // Printer Service & Counter States (Yazıcı Servis, Sayaç, Temizleme ve Atık Kutu Bilgileri)
  const [printerFirstPrintDate, setPrinterFirstPrintDate] = useState<string>(ticket.printerFirstPrintDate || '');
  const [printerCleaningCount, setPrinterCleaningCount] = useState<string | number>(
    ticket.printerCleaningCount !== undefined ? ticket.printerCleaningCount : ''
  );
  const [printerTotalPages, setPrinterTotalPages] = useState<string | number>(
    ticket.printerTotalPages !== undefined ? ticket.printerTotalPages : ''
  );
  const [printerWasteBoxInfo, setPrinterWasteBoxInfo] = useState<string>(ticket.printerWasteBoxInfo || '');
  const [isPrinterSectionOpen, setIsPrinterSectionOpen] = useState<boolean>(true);

  const customerTrackingUrl = useMemo(() => {
    return getCustomerTrackingUrl(ticket.id, ticket.customerPhone);
  }, [ticket.id, ticket.customerPhone]);

  const handleOpenCustomerTracking = () => {
    if (typeof window !== 'undefined') {
      window.open(customerTrackingUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCopyCustomerTrackingLink = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(customerTrackingUrl);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = customerTrackingUrl;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopiedTrackingLink(true);
      setTimeout(() => setCopiedTrackingLink(false), 2500);
    } catch (e) {
      console.error('Failed to copy tracking link', e);
    }
  };

  const allStoredUsers = useMemo(() => {
    return getStoredUsers().filter(u => u.isActive !== false);
  }, []);

  const isAssignedToCurrentUser = useMemo(() => {
    if (!currentUser) return false;
    return isTicketAssignedToUser(
      {
        ...ticket,
        assignedTechnicianId,
        assignedTechnicianName,
        assignedTechnicianUsername,
      },
      currentUser
    );
  }, [ticket, assignedTechnicianId, assignedTechnicianName, assignedTechnicianUsername, currentUser]);

  const handleAssignToMe = () => {
    if (!currentUser) return;
    const currentUserName = currentUser.name || currentUser.fullName || currentUser.username;
    const nowISO = new Date().toISOString();
    setAssignedTechnicianId(currentUser.id);
    setAssignedTechnicianName(currentUser.name);
    setAssignedTechnicianUsername(currentUser.username);
    setRepairedByName(currentUserName);
    setRepairedByUser(currentUser.username);
    setRepairedAt(nowISO);

    // Otomatik olarak Onarımda (Tamirde) durumuna al (eğer henüz hazır/teslim edilmemişse)
    let nextStatus = status;
    if (['Kaydedildi', 'Incelemede', 'MusteriOnayi', 'Onaylandi', 'ParcaBekliyor'].includes(status)) {
      nextStatus = 'Tamirde';
      setStatus('Tamirde');
    }

    const updated: ServiceTicket = {
      ...ticket,
      status: nextStatus,
      assignedTechnicianId: currentUser.id,
      assignedTechnicianName: currentUser.name,
      assignedTechnicianUsername: currentUser.username,
      repairedByName: currentUserName,
      repairedByUser: currentUser.username,
      repairedAt: nowISO,
      lastUpdatedByUser: currentUser.username,
      lastUpdatedByName: currentUserName,
      lastUpdatedAt: nowISO,
      updatedAt: nowISO,
    };
    onSave(updated);
    setSavedToast(true);
    setTimeout(() => setSavedToast(false), 2500);
  };

  const handleUnassign = () => {
    const currentUserName = currentUser?.name || currentUser?.fullName || currentUser?.username || 'Sistem';
    const currentUsername = currentUser?.username || 'admin';
    setAssignedTechnicianId('');
    setAssignedTechnicianName('');
    setAssignedTechnicianUsername('');

    const updated: ServiceTicket = {
      ...ticket,
      assignedTechnicianId: undefined,
      assignedTechnicianName: undefined,
      assignedTechnicianUsername: undefined,
      lastUpdatedByUser: currentUsername,
      lastUpdatedByName: currentUserName,
      lastUpdatedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    onSave(updated);
    setSavedToast(true);
    setTimeout(() => setSavedToast(false), 2500);
  };

  // Sub-Service (Alt / Dış Servis & Cari) states
  const [subServiceName, setSubServiceName] = useState<string>(ticket.subServiceName || '');
  const [subServiceCost, setSubServiceCost] = useState<number>(ticket.subServiceCost || 0);
  const [outsideServiceCost, setOutsideServiceCost] = useState<number>(ticket.outsideServiceCost || 0);
  const [subServiceTrackingNo, setSubServiceTrackingNo] = useState<string>(ticket.subServiceTrackingNo || '');
  const [subServiceNotes, setSubServiceNotes] = useState<string>(ticket.subServiceNotes || '');
  const [subServicePaymentStatus, setSubServicePaymentStatus] = useState<'Odendi' | 'Odenmedi' | 'KismiOdendi'>(
    ticket.subServicePaymentStatus || (ticket.subServiceCost && ticket.subServiceCost > 0 ? 'Odenmedi' : 'Odenmedi')
  );
  const [subServicePaidAmount, setSubServicePaidAmount] = useState<number>(
    ticket.subServicePaidAmount !== undefined
      ? ticket.subServicePaidAmount
      : ticket.subServicePaymentStatus === 'Odendi'
      ? ticket.subServiceCost || 0
      : 0
  );
  const [subServicePaidDate, setSubServicePaidDate] = useState<string>(ticket.subServicePaidDate || '');
  const [subServicePaymentMethod, setSubServicePaymentMethod] = useState<PaymentMethod>(ticket.subServicePaymentMethod || 'Nakit');
  const [subServicePaidNote, setSubServicePaidNote] = useState<string>(ticket.subServicePaidNote || '');
  const [showSubServiceSuggestions, setShowSubServiceSuggestions] = useState<boolean>(false);

  // Extract unique registered sub-services across all tickets
  const registeredSubServices = useMemo(() => {
    const map = new Map<string, RegisteredSubServiceItem>();
    if (!allTickets || allTickets.length === 0) return [];

    allTickets.forEach(t => {
      const sName = (t.subServiceName || '').trim();
      if (!sName) return;

      const cost = t.subServiceCost || 0;
      const isPaid = t.subServicePaymentStatus === 'Odendi';
      const debt = isPaid ? 0 : cost;

      const key = sName.toLowerCase();
      if (!map.has(key)) {
        map.set(key, {
          name: sName,
          ticketCount: 1,
          unpaidDebt: debt,
          totalSpent: cost,
          lastUsedDate: t.createdAt,
          lastUsedCost: cost > 0 ? cost : undefined,
        });
      } else {
        const item = map.get(key)!;
        item.ticketCount += 1;
        item.unpaidDebt += debt;
        item.totalSpent += cost;
        if (cost > 0) item.lastUsedCost = cost;
        if (t.createdAt && (!item.lastUsedDate || t.createdAt > item.lastUsedDate)) {
          item.lastUsedDate = t.createdAt;
        }
      }
    });

    return Array.from(map.values()).sort((a, b) => b.ticketCount - a.ticketCount);
  }, [allTickets]);

  // Filter matching sub-services based on input
  const matchingSubServices = useMemo(() => {
    if (!registeredSubServices || registeredSubServices.length === 0) return [];
    const q = subServiceName.trim().toLowerCase();
    if (!q) {
      return registeredSubServices.slice(0, 6);
    }
    return registeredSubServices.filter(s => s.name.toLowerCase().includes(q));
  }, [registeredSubServices, subServiceName]);

  const handleSelectSubService = (sub: RegisteredSubServiceItem) => {
    setSubServiceName(sub.name);
    setShowSubServiceSuggestions(false);
  };

  // New part input & inventory link states
  const [newPartName, setNewPartName] = useState('');
  const [newPartQtyStr, setNewPartQtyStr] = useState('1');
  const [newPartCost, setNewPartCost] = useState('');
  const [newPartPrice, setNewPartPrice] = useState('');
  const [newPartCurrency, setNewPartCurrency] = useState<'TRY' | 'USD'>('TRY');
  const [selectedInventoryId, setSelectedInventoryId] = useState<string>('');
  const [showInventorySuggestions, setShowInventorySuggestions] = useState<boolean>(false);
  const [partErrorMsg, setPartErrorMsg] = useState<string>('');

  // Filter matching inventory items
  const matchingInventoryItems = useMemo(() => {
    if (!inventory || inventory.length === 0) return [];
    const q = newPartName.trim().toLowerCase();
    if (!q) {
      return inventory.filter(i => i.quantity > 0).slice(0, 8);
    }
    return inventory.filter(
      i =>
        i.name.toLowerCase().includes(q) ||
        (i.code && i.code.toLowerCase().includes(q)) ||
        (i.category && i.category.toLowerCase().includes(q))
    ).slice(0, 8);
  }, [inventory, newPartName]);

  // Find linked or matching inventory item for the current input
  const selectedInvItem = useMemo(() => {
    if (selectedInventoryId) {
      return inventory.find(i => i.id === selectedInventoryId) || null;
    }
    if (!newPartName.trim()) return null;
    const q = newPartName.trim().toLowerCase();
    return (
      inventory.find(
        i =>
          i.name.trim().toLowerCase() === q ||
          (i.code && i.code.trim().toLowerCase() === q)
      ) || null
    );
  }, [selectedInventoryId, newPartName, inventory]);

  // Quantity of this inventory item already added in current ticket's parts list
  const alreadyAddedInParts = useMemo(() => {
    if (!selectedInvItem) return 0;
    return parts
      .filter(
        p =>
          p.inventoryItemId === selectedInvItem.id ||
          p.name.trim().toLowerCase() === selectedInvItem.name.trim().toLowerCase()
      )
      .reduce((acc, p) => acc + (p.quantity || 1), 0);
  }, [selectedInvItem, parts]);

  // Quantity originally in this ticket when loaded (saved ticket)
  const originalTicketQty = useMemo(() => {
    if (!ticket?.parts || !selectedInvItem) return 0;
    return ticket.parts
      .filter(
        p =>
          p.inventoryItemId === selectedInvItem.id ||
          p.name.trim().toLowerCase() === selectedInvItem.name.trim().toLowerCase()
      )
      .reduce((acc, p) => acc + (p.quantity || 1), 0);
  }, [ticket, selectedInvItem]);

  // Maximum allowed total in this ticket (warehouse stock + what was already reserved on this ticket)
  const maxStockAllowed = selectedInvItem ? (selectedInvItem.quantity || 0) + originalTicketQty : 999999;
  const remainingCanAdd = Math.max(0, maxStockAllowed - alreadyAddedInParts);

  const parsedPartQty = parseInt(newPartQtyStr, 10);
  const validNewPartQty = isNaN(parsedPartQty) ? 0 : parsedPartQty;

  const isInvOutOfStock = selectedInvItem !== null && (maxStockAllowed <= 0 || (selectedInvItem.quantity <= 0 && originalTicketQty <= 0));
  const isExceedingStock = selectedInvItem !== null && !isInvOutOfStock && (validNewPartQty > remainingCanAdd || alreadyAddedInParts + validNewPartQty > maxStockAllowed);

  const handleSelectInventoryItem = (item: InventoryItem) => {
    setNewPartName(item.name);
    const currentRate = getStoredExchangeRate().rate || 47.96;
    const sortedLots = getSortedLotsByLowestCost(item, currentRate);
    const activeLot = sortedLots[0];

    if (item.currency === 'USD' || (item.costPriceUSD !== undefined && (item.costPriceUSD || 0) > 0) || (activeLot && activeLot.currency === 'USD')) {
      setNewPartCurrency('USD');
      const costUSD = activeLot?.costPriceUSD !== undefined
        ? activeLot.costPriceUSD
        : (item.costPriceUSD !== undefined
            ? item.costPriceUSD
            : (item.costPrice ? item.costPrice / currentRate : 0));
      setNewPartCost(String(Math.round(costUSD * 100) / 100));
      setNewPartPrice(item.salePriceUSD !== undefined ? String(item.salePriceUSD) : String(Math.round((item.salePrice / currentRate) * 100) / 100 || ''));
    } else {
      setNewPartCurrency('TRY');
      const costTL = activeLot ? getLotCostTL(activeLot, currentRate) : (item.costPrice !== undefined ? item.costPrice : 0);
      setNewPartCost(String(costTL));
      setNewPartPrice(item.salePrice !== undefined ? String(item.salePrice) : '');
    }
    setSelectedInventoryId(item.id);
    setShowInventorySuggestions(false);
    setPartErrorMsg('');
  };

  // Find previous records for this specific device (by Serial, Related Ticket ID, or Customer & Model)
  const pastDeviceTickets = useMemo(() => {
    if (!ticket || !allTickets) return [];

    const currentPhone = ticket.customerPhone ? ticket.customerPhone.replace(/\D/g, '') : '';
    const currentName = ticket.customerName.trim().toLowerCase();
    const currentBrand = (ticket.deviceBrand || '').trim().toLowerCase();
    const currentModel = (ticket.deviceModel || '').trim().toLowerCase();
    const currentType = (ticket.deviceType || '').toLowerCase();
    const currentSerial = (ticket.serialNumber || '').trim().toLowerCase();
    const hasValidSerial =
      Boolean(currentSerial) &&
      currentSerial !== 'belirtilmedi' &&
      currentSerial !== 'yok' &&
      currentSerial !== '-' &&
      currentSerial.length > 2;

    const pastSameDevice: ServiceTicket[] = [];

    allTickets.forEach(t => {
      if (t.id === ticket.id) return;

      // 1. Direct link via relatedPreviousTicketId
      if (ticket.relatedPreviousTicketId && t.id === ticket.relatedPreviousTicketId) {
        pastSameDevice.push(t);
        return;
      }

      const tSerial = (t.serialNumber || '').trim().toLowerCase();
      const tHasValidSerial =
        Boolean(tSerial) &&
        tSerial !== 'belirtilmedi' &&
        tSerial !== 'yok' &&
        tSerial !== '-' &&
        tSerial.length > 2;

      // 2. Exact Serial Match (Highest confidence for hardware/printers)
      if (hasValidSerial && tHasValidSerial && currentSerial === tSerial) {
        pastSameDevice.push(t);
        return;
      }

      // 3. Customer match + brand/model match
      const tPhone = t.customerPhone ? t.customerPhone.replace(/\D/g, '') : '';
      const tName = t.customerName.trim().toLowerCase();
      const isCustomerMatch =
        (currentPhone && tPhone && currentPhone === tPhone) ||
        (currentName && tName && currentName === tName);

      if (isCustomerMatch) {
        // Only treat as same physical device if BOTH have valid matching serial numbers
        let isSameDevice = false;
        if (hasValidSerial && tHasValidSerial) {
          isSameDevice = currentSerial === tSerial;
        }

        if (isSameDevice && !pastSameDevice.some(x => x.id === t.id)) {
          pastSameDevice.push(t);
        }
      }
    });

    pastSameDevice.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return pastSameDevice;
  }, [ticket, allTickets]);

  // Is this device a printer or has printer properties?
  const isPrinterDevice = useMemo(() => {
    if (!ticket) return false;
    const dType = (ticket.deviceType || '').toLowerCase();
    const dBrand = (ticket.deviceBrand || '').toLowerCase();
    const dModel = (ticket.deviceModel || '').toLowerCase();
    return (
      dType.includes('yazıcı') ||
      dType.includes('yazici') ||
      dType.includes('printer') ||
      dType.includes('tarayıcı') ||
      dType.includes('tarayici') ||
      dType.includes('plotter') ||
      dType.includes('fotokopi') ||
      dBrand.includes('epson') ||
      dBrand.includes('brother') ||
      dBrand.includes('canon') ||
      dBrand.includes('xerox') ||
      dBrand.includes('kyocera') ||
      dBrand.includes('pantum') ||
      dBrand.includes('oki') ||
      dBrand.includes('zebra') ||
      dModel.includes('laserjet') ||
      dModel.includes('deskjet') ||
      dModel.includes('ink tank') ||
      dModel.includes('ecotank') ||
      dModel.includes('smart tank') ||
      Boolean(ticket.printerFirstPrintDate || ticket.printerCleaningCount !== undefined || ticket.printerTotalPages !== undefined || ticket.printerWasteBoxInfo)
    );
  }, [ticket]);

  // Find the previous ticket that contains printer data (or the most recent past ticket)
  const previousPrinterTicket = useMemo(() => {
    if (!pastDeviceTickets || pastDeviceTickets.length === 0) return null;
    const pastWithPrinter = pastDeviceTickets.find(
      t =>
        Boolean(t.printerFirstPrintDate) ||
        (t.printerCleaningCount !== undefined && t.printerCleaningCount !== '') ||
        (t.printerTotalPages !== undefined && t.printerTotalPages !== '') ||
        Boolean(t.printerWasteBoxInfo)
    );
    if (pastWithPrinter) return pastWithPrinter;
    return pastDeviceTickets[0] || null;
  }, [pastDeviceTickets]);

  // Compute print page difference
  const printerPageDifference = useMemo(() => {
    if (!previousPrinterTicket?.printerTotalPages || printerTotalPages === '' || printerTotalPages === undefined) return null;
    const prevClean = String(previousPrinterTicket.printerTotalPages).replace(/\D/g, '');
    const currClean = String(printerTotalPages).replace(/\D/g, '');
    const prevNum = parseInt(prevClean, 10);
    const currNum = parseInt(currClean, 10);
    if (isNaN(prevNum) || isNaN(currNum)) return null;
    return currNum - prevNum;
  }, [previousPrinterTicket, printerTotalPages]);

  // Compute cleaning count difference
  const printerCleaningDifference = useMemo(() => {
    if (!previousPrinterTicket?.printerCleaningCount || printerCleaningCount === '' || printerCleaningCount === undefined) return null;
    const prevMatches = String(previousPrinterTicket.printerCleaningCount).match(/\d+/g);
    const currMatches = String(printerCleaningCount).match(/\d+/g);
    if (!prevMatches || !currMatches) return null;
    const prevSum = prevMatches.reduce((acc, val) => acc + parseInt(val, 10), 0);
    const currSum = currMatches.reduce((acc, val) => acc + parseInt(val, 10), 0);
    if (isNaN(prevSum) || isNaN(currSum)) return null;
    return currSum - prevSum;
  }, [previousPrinterTicket, printerCleaningCount]);

  useEffect(() => {
    if (ticket) {
      setStatus(ticket.status);
      setPerformedActions(ticket.performedActions || '');
      setTechnicianNotes(cleanTechnicianNotes(ticket.technicianNotes));
      setIsUnderWarranty(ticket.isUnderWarranty);
      setDeviceType(ticket.deviceType || '');
      setLaborCost(ticket.laborCost || 0);
      setVatRate(20);
      setIsVatIncluded(ticket.isVatIncluded || false);
      setDiscount(ticket.discount || 0);
      setRoundingAdjustment(0);
      
      // Printer info sync
      setPrinterFirstPrintDate(ticket.printerFirstPrintDate || '');
      setPrinterCleaningCount(ticket.printerCleaningCount !== undefined ? ticket.printerCleaningCount : '');
      setPrinterTotalPages(ticket.printerTotalPages !== undefined ? ticket.printerTotalPages : '');
      setPrinterWasteBoxInfo(ticket.printerWasteBoxInfo || '');
      
      const split = getTicketPaymentSplit(ticket);
      setCashAmount(split.cash);
      setCardAmount(split.card);
      setTransferAmount(split.transfer);

      const isContract = ticket.serviceType === 'Sozlesmeli' || Boolean(ticket.contractedCompanyId);
      const initialBreakdown = getTicketContractBreakdown(ticket);

      if (isContract && !initialBreakdown.hasOutOfContractParts) {
        setPaymentStatus('Odendi');
      } else {
        setPaymentStatus(ticket.paymentStatus);
      }
      setPaymentMethod(ticket.paymentMethod);
      setParts(ticket.parts || []);
      setIncludedAccessories(ticket.includedAccessories || []);
      setDeliveryDateInput(isoToDatetimeLocal(ticket.deliveryDate));
      setWarrantyDays(ticket.warrantyDays !== undefined ? ticket.warrantyDays : 30);
      setSubServiceName(ticket.subServiceName || '');
      setSubServiceCost(ticket.subServiceCost || 0);
      setOutsideServiceCost(ticket.outsideServiceCost || 0);
      setSubServiceTrackingNo(ticket.subServiceTrackingNo || '');
      setSubServiceNotes(ticket.subServiceNotes || '');
      setSubServicePaymentStatus(
        ticket.subServicePaymentStatus || (ticket.subServiceCost && ticket.subServiceCost > 0 ? 'Odenmedi' : 'Odenmedi')
      );
      setSubServicePaidAmount(
        ticket.subServicePaidAmount !== undefined
          ? ticket.subServicePaidAmount
          : ticket.subServicePaymentStatus === 'Odendi'
          ? ticket.subServiceCost || 0
          : 0
      );
      setSubServicePaidDate(ticket.subServicePaidDate || '');
      setSubServicePaymentMethod(ticket.subServicePaymentMethod || 'Nakit');
      setSubServicePaidNote(ticket.subServicePaidNote || '');

      // Personnel tracking state sync
      setAssignedTechnicianId(ticket.assignedTechnicianId || '');
      setAssignedTechnicianName(ticket.assignedTechnicianName || '');
      setAssignedTechnicianUsername(ticket.assignedTechnicianUsername || '');
      setRepairedByName(ticket.repairedByName || ticket.assignedTechnicianName || '');
      setRepairedByUser(ticket.repairedByUser || ticket.assignedTechnicianUsername || '');
      setRepairedAt(ticket.repairedAt || '');
      setDeliveredByName(ticket.deliveredByName || (ticket.status === 'TeslimEdildi' || ticket.status === 'IadeEdildi' ? (currentUser?.name || '') : ''));
      setDeliveredByUser(ticket.deliveredByUser || (ticket.status === 'TeslimEdildi' || ticket.status === 'IadeEdildi' ? (currentUser?.username || '') : ''));
      setDeliveredAt(ticket.deliveredAt || ticket.deliveryDate || '');
    }
  }, [ticket]);

  const isLimitedTechnician = currentUser?.role === 'limited_technician';
  const isNormalTechnician = currentUser?.role === 'technician' || isTechnician;
  const isAdmin = currentUser?.role === 'admin' || (!currentUser && !isLimitedTechnician && !isNormalTechnician);

  const isLocked = ticket.status === 'TeslimEdildi' || ticket.status === 'IadeEdildi';
  // Karşılama yetkilisi sadece teslim/iade durumu ve ödeme tahsilatı yapabilir; parça, maliyet, işlem notu değiştiremez.
  const canModifyDetails = !isLocked && !isLimitedTechnician;

  const handleAddAccessory = () => {
    if (!canModifyDetails) return;
    const trimmed = newAccessoryInput.trim();
    if (!trimmed) return;
    if (!includedAccessories.includes(trimmed)) {
      setIncludedAccessories([...includedAccessories, trimmed]);
    }
    setNewAccessoryInput('');
  };

  const handleRemoveAccessory = (accToRemove: string) => {
    if (!canModifyDetails) return;
    setIncludedAccessories(includedAccessories.filter(a => a !== accToRemove));
    if (editingAccessoryIdx !== null) {
      setEditingAccessoryIdx(null);
    }
  };

  const handleStartEditAccessory = (idx: number, text: string) => {
    if (!canModifyDetails) return;
    setEditingAccessoryIdx(idx);
    setEditingAccessoryText(text);
  };

  const handleSaveEditAccessory = (idx: number) => {
    if (!canModifyDetails) return;
    const trimmed = editingAccessoryText.trim();
    if (trimmed) {
      const updated = [...includedAccessories];
      updated[idx] = trimmed;
      setIncludedAccessories(updated);
    }
    setEditingAccessoryIdx(null);
    setEditingAccessoryText('');
  };

  const handleStatusChange = (newStatus: TicketStatus) => {
    if (isLocked) return;
    if (
      isLimitedTechnician &&
      newStatus !== 'TeslimEdildi' &&
      newStatus !== 'IadeEdildi' &&
      newStatus !== 'Onaylandi' &&
      newStatus !== 'Onaylanmadi' &&
      newStatus !== 'MusteriOnayi'
    ) {
      alert('Karşılama rolü yalnızca Onaylandı, Onaylanmadı, Onay Bekliyor, Teslim Edildi veya İade Edildi durumlarına güncelleme yapabilir.');
      return;
    }

    setStatus(newStatus);
    const nowISO = new Date().toISOString();
    // Durum değiştiğinde işlem / teslim tarihini otomatik güncel zamana ayarla
    setDeliveryDateInput(isoToDatetimeLocal(nowISO));

    const currentUserName = currentUser?.name || currentUser?.fullName || currentUser?.username || 'Sistem Yöneticisi';
    const currentUsername = currentUser?.username || 'admin';

    if (newStatus === 'TeslimEdildi' || newStatus === 'IadeEdildi') {
      if (!deliveredByName) {
        setDeliveredByName(currentUserName);
        setDeliveredByUser(currentUsername);
        setDeliveredAt(nowISO);
      }
    } else if (['Tamirde', 'Incelemede', 'TestEdiliyor', 'Sivida', 'Hazir', 'MusteriOnayi', 'ParcaBekliyor', 'Onaylandi'].includes(newStatus)) {
      if (!repairedByName) {
        setRepairedByName(assignedTechnicianName || currentUserName);
        setRepairedByUser(assignedTechnicianUsername || currentUsername);
        setRepairedAt(nowISO);
      }
    }
  };

  const { partsTotal, subtotal, vatAmount, grandTotal } = calculateTicketTotal(parts, laborCost, vatRate, discount, isVatIncluded, roundingAdjustment);
  const partsCostTotal = calculateTicketPartsCost(parts);

  const isContractedTicket = ticket?.serviceType === 'Sozlesmeli' || Boolean(ticket?.contractedCompanyId);

  const contractBreakdown = useMemo(() => {
    if (!ticket) {
      return {
        inContractParts: [],
        outOfContractParts: [],
        inContractGrandTotal: 0,
        outOfContractTotal: 0,
        outOfContractCost: 0,
        hasOutOfContractParts: false,
        extraDebt: 0,
        effectiveDebt: 0,
      };
    }
    return getTicketContractBreakdown({
      ...ticket,
      serviceType: isContractedTicket ? 'Sozlesmeli' : ticket.serviceType,
      parts,
      laborCost,
      vatRate,
      discount,
      isVatIncluded,
      paidAmount: totalPaid,
      paymentBreakdown: {
        cash: Math.max(0, cashAmount || 0),
        card: Math.max(0, cardAmount || 0),
        transfer: Math.max(0, transferAmount || 0),
      },
    });
  }, [ticket, isContractedTicket, parts, laborCost, vatRate, discount, isVatIncluded, totalPaid, cashAmount, cardAmount, transferAmount]);

  const targetPayableAmount = isContractedTicket ? contractBreakdown.outOfContractTotal : grandTotal;
  const remainingDebtAmount = Math.max(0, targetPayableAmount - totalPaid);

  const handleTogglePartOutOfContract = (partId: string) => {
    if (!canModifyDetails) return;
    setParts(prev =>
      prev.map(p => (p.id === partId ? { ...p, isOutOfContract: !p.isOutOfContract } : p))
    );
  };

  const handleAddPart = () => {
    if (!canModifyDetails) return;
    setPartErrorMsg('');

    const trimmedName = newPartName.trim();
    if (!trimmedName) {
      setPartErrorMsg('Lütfen eklenecek parçanın adını giriniz veya stok listesinden seçiniz.');
      return;
    }

    if (validNewPartQty <= 0) {
      setPartErrorMsg('Lütfen en az 1 adet miktar giriniz.');
      return;
    }

    if (!newPartPrice) {
      setPartErrorMsg('Lütfen parça satış fiyatını giriniz.');
      return;
    }

    const priceNum = parseFloat(newPartPrice);
    if (isNaN(priceNum) || priceNum < 0) {
      setPartErrorMsg('Lütfen geçerli bir satış fiyatı giriniz.');
      return;
    }
    const costNum = newPartCost ? parseFloat(newPartCost) || 0 : 0;

    const currentRate = getStoredExchangeRate().rate || 47.96;
    const isUSD = newPartCurrency === 'USD';
    
    // Find active lowest cost lot if inventory item is selected
    let costRate = isUSD
      ? (selectedInvItem?.costExchangeRate || selectedInvItem?.exchangeRate || currentRate)
      : undefined;
    let selectedLotId: string | undefined = undefined;

    if (selectedInvItem) {
      const sortedLots = getSortedLotsByLowestCost(selectedInvItem, currentRate);
      if (sortedLots.length > 0) {
        const bestLot = sortedLots[0];
        selectedLotId = bestLot.id;
        if (isUSD && bestLot.exchangeRate && bestLot.exchangeRate > 0) {
          costRate = bestLot.exchangeRate;
        }
      }
    }

    const saleRate = isUSD ? currentRate : undefined; // Satış daima güncel canlı kur üzerinden hesaplanır
    const finalPriceTL = isUSD ? convertUSDToTL(priceNum, currentRate) : priceNum;
    const finalCostTL = isUSD ? convertUSDToTL(costNum, costRate || currentRate) : costNum;

    // Strict Inventory Stock Validation
    if (selectedInvItem) {
      if (selectedInvItem.quantity <= 0 && originalTicketQty <= 0) {
        setPartErrorMsg(`Stok Yetersiz! '${selectedInvItem.name}' stokta 0 adet olduğundan eklenemez.`);
        return;
      }

      if (validNewPartQty > remainingCanAdd) {
        if (remainingCanAdd <= 0) {
          setPartErrorMsg(
            `Yetersiz Stok! '${selectedInvItem.name}' parçası için mevcut stok (${selectedInvItem.quantity} adet) zaten forma eklenmiştir. Daha fazla eklenemez.`
          );
        } else {
          setPartErrorMsg(
            `Yetersiz Stok! '${selectedInvItem.name}' için depoda ${selectedInvItem.quantity} adet bulunmaktadır. Forma en fazla ${remainingCanAdd} adet daha eklenebilir (Girilen: ${validNewPartQty} adet).`
          );
        }
        return;
      }
    }

    // Add as a new distinct line item (allowing same part to be added multiple times with separate contract/out-of-contract status)
    const newPart: ServicePart = {
      id: 'p_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7),
      name: trimmedName,
      code: selectedInvItem?.code || selectedInvItem?.id || undefined,
      quantity: validNewPartQty,
      unitPrice: finalPriceTL,
      costPrice: finalCostTL,
      currency: isUSD ? 'USD' : 'TRY',
      unitPriceUSD: isUSD ? priceNum : undefined,
      costPriceUSD: isUSD ? costNum : undefined,
      exchangeRate: isUSD ? currentRate : undefined,
      costExchangeRate: costRate,
      saleExchangeRate: saleRate,
      inventoryItemId: selectedInvItem?.id || undefined,
      lotId: selectedLotId,
      isOutOfContract: false,
    };
    setParts([...parts, newPart]);

    setNewPartName('');
    setNewPartQtyStr('1');
    setNewPartCost('');
    setNewPartPrice('');
    setSelectedInventoryId('');
    setShowInventorySuggestions(false);
    setPartErrorMsg('');
  };

  const handleAdjustPartQty = (partId: string, delta: number) => {
    if (!canModifyDetails) return;
    setPartErrorMsg('');

    const targetPart = parts.find(p => p.id === partId);
    if (!targetPart) return;

    const currentQty = targetPart.quantity || 1;
    const newQty = currentQty + delta;

    if (newQty <= 0) {
      handleRemovePart(partId);
      return;
    }

    // If linked to inventory, check stock limit
    const invId = targetPart.inventoryItemId;
    const invItem = invId
      ? inventory.find(i => i.id === invId)
      : inventory.find(i => i.name.trim().toLowerCase() === targetPart.name.trim().toLowerCase());

    if (invItem && delta > 0) {
      const oldQtyOnTicket = (ticket?.parts || [])
        .filter(p => p.inventoryItemId === invItem.id || p.name.trim().toLowerCase() === invItem.name.trim().toLowerCase())
        .reduce((sum, p) => sum + (p.quantity || 1), 0);

      const otherPartsUsed = parts
        .filter(p => p.id !== partId && (p.inventoryItemId === invItem.id || p.name.trim().toLowerCase() === invItem.name.trim().toLowerCase()))
        .reduce((sum, p) => sum + (p.quantity || 1), 0);

      const maxAllowed = invItem.quantity + oldQtyOnTicket;
      if (otherPartsUsed + newQty > maxAllowed) {
        setPartErrorMsg(
          `Yetersiz Stok! '${invItem.name}' için mevcut stok (${invItem.quantity} adet) aşılamaz. En fazla ${maxAllowed} adet kullanılabilir.`
        );
        return;
      }
    }

    setParts(parts.map(p => (p.id === partId ? { ...p, quantity: newQty } : p)));
  };

  const handleRemovePart = (id: string) => {
    if (!canModifyDetails) return;
    setPartErrorMsg('');
    setParts(parts.filter(p => p.id !== id));
  };

  const handleSave = () => {
    if (isLocked) return;
    setPartErrorMsg('');

    // Pre-save validation: Check if any part in ticket exceeds inventory stock
    for (const p of parts) {
      const invId = p.inventoryItemId;
      const invItem = invId
        ? inventory.find(i => i.id === invId)
        : inventory.find(i => i.name.trim().toLowerCase() === p.name.trim().toLowerCase());

      if (invItem) {
        const totalUsedInTicket = parts
          .filter(x => x.inventoryItemId === invItem.id || x.name.trim().toLowerCase() === invItem.name.trim().toLowerCase())
          .reduce((acc, x) => acc + (x.quantity || 1), 0);

        const oldQtyOnTicket = (ticket?.parts || [])
          .filter(x => x.inventoryItemId === invItem.id || x.name.trim().toLowerCase() === invItem.name.trim().toLowerCase())
          .reduce((acc, x) => acc + (x.quantity || 1), 0);

        const maxAllowed = invItem.quantity + oldQtyOnTicket;
        if (totalUsedInTicket > maxAllowed) {
          const errMsg = `Stok Yetersiz! '${invItem.name}' için mevcut stok (${invItem.quantity} adet) yetersizdir. Forma toplam ${totalUsedInTicket} adet eklenmiş. Lütfen parça miktarını düzeltiniz.`;
          setPartErrorMsg(errMsg);
          alert(errMsg);
          return;
        }
      }
    }

    const nowISO = new Date().toISOString();
    const finalDeliveryDate = nowISO;
    setDeliveryDateInput(isoToDatetimeLocal(nowISO));

    // Compute effective payment status & paid amounts
    let finalPaymentStatus: PaymentStatus = paymentStatus;
    let finalPaidAmount = totalPaid;
    let effectiveCash = Math.max(0, cashAmount || 0);
    let effectiveCard = Math.max(0, cardAmount || 0);
    let effectiveTransfer = Math.max(0, transferAmount || 0);

    if (isContractedTicket) {
      if (!contractBreakdown.hasOutOfContractParts) {
        // Sözleşme kapsamındaki bakiye cari sözleşmeden mahsup edilir ve otomatik ödendi sayılır.
        // Ödeme alanları kesinlikle 0 kalır (kasaya nakit/kart tahsilatı girilmez).
        finalPaymentStatus = 'Odendi';
        finalPaidAmount = 0;
        effectiveCash = 0;
        effectiveCard = 0;
        effectiveTransfer = 0;
      } else {
        // Sözleşme dışı ekstra parça varsa onun ödeme durumuna bakılır
        if (totalPaid >= contractBreakdown.outOfContractTotal - 0.01 && contractBreakdown.outOfContractTotal > 0) {
          finalPaymentStatus = 'Odendi';
        } else if (totalPaid > 0) {
          finalPaymentStatus = 'KismiOdendi';
        } else if (paymentStatus !== 'IadeEdildi') {
          finalPaymentStatus = 'Bekliyor';
        }
      }
    } else {
      if (isUnderWarranty && grandTotal <= 0 && totalPaid <= 0) {
        finalPaymentStatus = 'Odendi';
      } else if (totalPaid >= grandTotal - 0.01 && grandTotal > 0) {
        finalPaymentStatus = 'Odendi';
      } else if (totalPaid > 0) {
        finalPaymentStatus = 'KismiOdendi';
      } else if (paymentStatus !== 'IadeEdildi') {
        finalPaymentStatus = 'Bekliyor';
      }
    }

    // Determine representative payment method
    let finalPaymentMethod: PaymentMethod = 'Nakit';
    const nonZeroMethods: string[] = [];
    if (effectiveCash > 0) nonZeroMethods.push('Nakit');
    if (effectiveCard > 0) nonZeroMethods.push('Kredi Karti');
    if (effectiveTransfer > 0) nonZeroMethods.push('EFT/Havale');

    if (nonZeroMethods.length > 1) {
      finalPaymentMethod = 'Karma';
    } else if (nonZeroMethods.length === 1) {
      finalPaymentMethod = nonZeroMethods[0] as PaymentMethod;
    } else if (paymentMethod) {
      finalPaymentMethod = paymentMethod;
    }

    // Payment history calculation:
    // If ticket already has payment records, preserve them and their original dates.
    // If amount is increased (e.g. card was 500 TL, now 1500 TL), add a new PaymentRecord with difference (+1000 TL) and current date nowISO.
    let basePayments: PaymentRecord[] = Array.isArray(ticket.payments) ? [...ticket.payments] : [];

    const prevSplit = getTicketPaymentSplit(ticket);
    const prevDate = ticket.deliveryDate || ticket.createdAt || nowISO;

    if (basePayments.length === 0 && (prevSplit.cash > 0 || prevSplit.card > 0 || prevSplit.transfer > 0)) {
      if (prevSplit.cash > 0) {
        basePayments.push({
          id: 'pay_cash_' + ticket.id + '_init',
          amount: prevSplit.cash,
          method: 'Nakit',
          date: prevDate,
          note: 'Nakit Tahsilat',
        });
      }
      if (prevSplit.card > 0) {
        basePayments.push({
          id: 'pay_card_' + ticket.id + '_init',
          amount: prevSplit.card,
          method: 'Kredi Karti',
          date: prevDate,
          note: 'Kredi Kartı / POS Tahsilat',
        });
      }
      if (prevSplit.transfer > 0) {
        basePayments.push({
          id: 'pay_eft_' + ticket.id + '_init',
          amount: prevSplit.transfer,
          method: 'EFT/Havale',
          date: prevDate,
          note: 'EFT / Havale Tahsilat',
        });
      }
    }

    const reconcilePayments = (
      currentList: PaymentRecord[],
      targetMethod: 'Nakit' | 'Kredi Karti' | 'EFT/Havale',
      targetAmount: number,
      defaultNote: string
    ): PaymentRecord[] => {
      const matching = currentList.filter(p => normalizePaymentMethod(p.method) === targetMethod);
      const nonMatching = currentList.filter(p => normalizePaymentMethod(p.method) !== targetMethod);
      const currentSum = matching.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
      const diff = Math.round((targetAmount - currentSum) * 100) / 100;

      if (diff > 0.009) {
        // Amount increased (e.g. 500 TL -> 1500 TL, diff is 1000 TL)
        // Keep all existing payments with original dates, append new diff with current timestamp!
        const newRecord: PaymentRecord = {
          id: 'pay_' + targetMethod.toLowerCase().replace(/[^a-z]/g, '') + '_' + Date.now() + '_' + Math.random().toString(36).substring(2, 6),
          amount: diff,
          method: targetMethod,
          date: nowISO,
          note: defaultNote,
        };
        return [...nonMatching, ...matching, newRecord];
      } else if (diff < -0.009) {
        // Amount decreased (e.g. user corrected/reduced an amount)
        let rem = targetAmount;
        const adjusted: PaymentRecord[] = [];
        if (rem > 0) {
          for (const p of matching) {
            if (rem <= 0) break;
            const pAmt = Number(p.amount) || 0;
            if (pAmt <= rem) {
              adjusted.push(p);
              rem -= pAmt;
            } else {
              adjusted.push({ ...p, amount: rem });
              rem = 0;
            }
          }
        }
        return [...nonMatching, ...adjusted];
      }
      return currentList;
    };

    let updatedPayments: PaymentRecord[] = [];
    if (isContractedTicket && !contractBreakdown.hasOutOfContractParts) {
      updatedPayments = [];
    } else {
      updatedPayments = reconcilePayments(basePayments, 'Nakit', effectiveCash, 'Nakit Tahsilat');
      updatedPayments = reconcilePayments(updatedPayments, 'Kredi Karti', effectiveCard, 'Kredi Kartı / POS Tahsilat');
      updatedPayments = reconcilePayments(updatedPayments, 'EFT/Havale', effectiveTransfer, 'EFT / Havale Tahsilat');
    }

    const effectiveSubServicePaidAmount =
      subServiceCost && subServiceCost > 0
        ? subServicePaymentStatus === 'Odendi'
          ? (subServicePaidAmount > 0 ? subServicePaidAmount : subServiceCost)
          : subServicePaymentStatus === 'KismiOdendi'
          ? subServicePaidAmount
          : 0
        : 0;

    const currentUserName = currentUser?.name || currentUser?.fullName || currentUser?.username || 'Sistem Yöneticisi';
    const currentUsername = currentUser?.username || 'admin';

    // Distinct Personnel Calculation
    const isRepairActivity =
      performedActions.trim().length > 0 ||
      parts.length > 0 ||
      laborCost > 0 ||
      technicianNotes.trim().length > 0 ||
      ['Tamirde', 'Incelemede', 'Hazir', 'MusteriOnayi', 'ParcaBekliyor', 'Onaylandi', 'TeslimEdildi'].includes(status);

    const finalRepairedName = repairedByName || assignedTechnicianName || (isRepairActivity ? currentUserName : undefined);
    const finalRepairedUser = repairedByUser || assignedTechnicianUsername || (isRepairActivity ? currentUsername : undefined);
    const finalRepairedAt = finalRepairedName ? (repairedAt || ticket.repairedAt || nowISO) : undefined;

    const isDeliveredOrReturned = status === 'TeslimEdildi' || status === 'IadeEdildi';
    const finalDeliveredName = isDeliveredOrReturned
      ? (deliveredByName || ticket.deliveredByName || currentUserName)
      : ticket.deliveredByName;
    const finalDeliveredUser = isDeliveredOrReturned
      ? (deliveredByUser || ticket.deliveredByUser || currentUsername)
      : ticket.deliveredByUser;
    const finalDeliveredAt = isDeliveredOrReturned
      ? (deliveredAt || ticket.deliveredAt || finalDeliveryDate)
      : ticket.deliveredAt;

    const updatedTicket: ServiceTicket = isLimitedTechnician
      ? {
          ...ticket,
          updatedAt: nowISO,
          deliveryDate: finalDeliveryDate,
          status,
          paymentStatus: finalPaymentStatus,
          paidAmount: isContractedTicket && !contractBreakdown.hasOutOfContractParts ? 0 : finalPaidAmount,
          paymentMethod: isContractedTicket && !contractBreakdown.hasOutOfContractParts ? 'Nakit' : finalPaymentMethod,
          paymentBreakdown: {
            cash: effectiveCash,
            card: effectiveCard,
            transfer: effectiveTransfer,
          },
          payments: updatedPayments,
          paymentDate: updatedPayments.length > 0 ? updatedPayments[updatedPayments.length - 1].date : undefined,
          // Personnel Audit Tracking
          lastUpdatedByUser: currentUsername,
          lastUpdatedByName: currentUserName,
          lastUpdatedAt: nowISO,
          deliveredByUser: finalDeliveredUser,
          deliveredByName: finalDeliveredName,
          deliveredAt: finalDeliveredAt,
        }
      : {
          ...ticket,
          createdAt: ticket.createdAt,
          updatedAt: nowISO,
          deliveryDate: finalDeliveryDate,
          status,
          performedActions: performedActions.trim() || undefined,
          technicianNotes: technicianNotes.trim() || undefined,
          isUnderWarranty,
          deviceType,
          laborCost,
          vatRate: 20,
          isVatIncluded: false,
          discount,
          roundingAdjustment: 0,
          totalAmount: grandTotal,
          paymentStatus: finalPaymentStatus,
          paidAmount: isContractedTicket && !contractBreakdown.hasOutOfContractParts ? 0 : finalPaidAmount,
          paymentMethod: isContractedTicket && !contractBreakdown.hasOutOfContractParts ? 'Nakit' : finalPaymentMethod,
          paymentBreakdown: {
            cash: effectiveCash,
            card: effectiveCard,
            transfer: effectiveTransfer,
          },
          payments: updatedPayments,
          paymentDate: updatedPayments.length > 0 ? updatedPayments[updatedPayments.length - 1].date : undefined,
          parts,
          includedAccessories,
          devicePassword: devicePassword.trim() || undefined,
          warrantyDays,
          assignedTechnicianId: assignedTechnicianId || undefined,
          assignedTechnicianName: assignedTechnicianName || undefined,
          assignedTechnicianUsername: assignedTechnicianUsername || undefined,
          // Personnel Audit Tracking: Kayıt Açan, Onaran, Teslim Eden, Son İşlem Yapan
          createdByUser: ticket.createdByUser || currentUsername,
          createdByName: ticket.createdByName || currentUserName,
          repairedByUser: finalRepairedUser,
          repairedByName: finalRepairedName,
          repairedAt: finalRepairedAt,
          deliveredByUser: finalDeliveredUser,
          deliveredByName: finalDeliveredName,
          deliveredAt: finalDeliveredAt,
          lastUpdatedByUser: currentUsername,
          lastUpdatedByName: currentUserName,
          lastUpdatedAt: nowISO,
          subServiceName: subServiceName.trim() || undefined,
          subServiceCost: subServiceCost || 0,
          outsideServiceCost: outsideServiceCost || 0,
          subServiceTrackingNo: subServiceTrackingNo.trim() || undefined,
          subServiceNotes: subServiceNotes.trim() || undefined,
          subServicePaymentStatus: (subServiceCost && subServiceCost > 0) ? subServicePaymentStatus : undefined,
          subServicePaidAmount: (subServiceCost && subServiceCost > 0) ? effectiveSubServicePaidAmount : undefined,
          subServicePaidDate: (subServicePaymentStatus === 'Odendi' || subServicePaymentStatus === 'KismiOdendi')
            ? (subServicePaidDate || new Date().toISOString())
            : undefined,
          subServicePaymentMethod: (subServicePaymentStatus === 'Odendi' || subServicePaymentStatus === 'KismiOdendi')
            ? subServicePaymentMethod
            : undefined,
          subServicePaidNote: subServicePaidNote.trim() || undefined,
          // Printer Service & Counter Info
          printerFirstPrintDate: printerFirstPrintDate.trim() || undefined,
          printerCleaningCount: printerCleaningCount !== '' ? printerCleaningCount : undefined,
          printerTotalPages: printerTotalPages !== '' ? printerTotalPages : undefined,
          printerWasteBoxInfo: printerWasteBoxInfo.trim() || undefined,
        };

    onSave(updatedTicket);
    setSavedToast(true);
    setTimeout(() => {
      setSavedToast(false);
    }, 3000);
  };

  const handleDelete = () => {
    if (!isAdmin) {
      alert('Sadece yönetici (admin) hesaplarının servis kaydı silme yetkisi bulunmaktadır.');
      return;
    }
    if (isLocked) return;
    if (confirm(`${ticket.id} numaralı servis kaydını sistemden gizlemek istediğinize emin misiniz? Kayıt veritabanında korunacaktır.`)) {
      onDeleteTicket(ticket.id);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/70 backdrop-blur-sm">
      <div className="min-h-full flex items-center justify-center p-3 sm:p-4 text-center">
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-6xl xl:max-w-7xl border border-slate-200 overflow-hidden my-auto text-left transform transition-all">
        {/* Saved Success Notification Toast Banner */}
        {savedToast && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50 bg-emerald-600 text-white px-5 py-2 rounded-full shadow-2xl flex items-center space-x-2 text-xs font-bold border border-emerald-400 animate-in fade-in slide-in-from-top-2 duration-300">
            <Check className="w-4 h-4 text-white shrink-0" />
            <span>Değişiklikler başarıyla kaydedildi!</span>
          </div>
        )}

        {/* Top Header */}
        <div className="bg-slate-900 text-white px-6 py-4 flex flex-wrap items-center justify-between border-b border-slate-800 gap-2">
          <div className="flex items-center space-x-3">
            <div className="px-2.5 py-1 bg-blue-600 rounded-md font-mono font-bold text-sm tracking-wide text-white">
              {ticket.id}
            </div>
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <span>{ticket.customerName}</span>
                <span className="text-xs text-slate-400 font-normal">({formatPhoneDisplay(ticket.customerPhone)})</span>
              </h2>
              <p className="text-xs text-slate-400">
                {deviceType} • {ticket.deviceBrand} {ticket.deviceModel} • Kayıt: {formatDateTR(ticket.createdAt)}
                {(ticket.createdByName || ticket.createdByUser) && (
                  <span className="text-blue-300 ml-1">
                    • Kaydı Alan: {ticket.createdByName || ticket.createdByUser}
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleOpenCustomerTracking}
              title="Müşteri Cihaz Takip Sayfasını Aç (Form No ve Telefon Otomatik Tanımlı)"
              className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-semibold flex items-center space-x-1.5 shadow-sm transition-colors cursor-pointer"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Müşteri Takip</span>
            </button>

            <button
              onClick={() => (onPrintLabel ? onPrintLabel(ticket) : onPrintIntake(ticket))}
              title="Cihaz & Barkod Etiketi Bas"
              className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-xs font-semibold flex items-center space-x-1.5 shadow-sm transition-colors cursor-pointer"
            >
              <Tag className="w-3.5 h-3.5" />
              <span>Etiket Bas</span>
            </button>

            {onOpenSms && (
              <button
                onClick={() => onOpenSms(ticket)}
                title="Müşteriye SMS Bilgilendirmesi Gönder"
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-semibold flex items-center space-x-1.5 shadow-sm transition-colors cursor-pointer"
              >
                <MessageSquare className="w-3.5 h-3.5" />
                <span>SMS Gönder</span>
              </button>
            )}

            <button
              onClick={() => onPrintIntake(ticket)}
              title="Kabul Formu Yazdır"
              className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded-lg text-xs font-semibold flex items-center space-x-1.5 transition-colors cursor-pointer"
            >
              <Printer className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Kabul Formu</span>
            </button>

            <button
              onClick={() => onPrintInvoice(ticket)}
              title="Fatura / Makbuz Önizle ve Yazdır"
              className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg text-xs font-semibold flex items-center space-x-1.5 shadow-sm transition-colors cursor-pointer"
            >
              <Receipt className="w-3.5 h-3.5" />
              <span>Fatura / Makbuz</span>
            </button>

            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Main Body */}
        <div className="p-3.5 sm:p-6 space-y-4 sm:space-y-6 max-h-[78vh] sm:max-h-[82vh] overflow-y-auto bg-slate-50/50">
          {/* Locked Notification Alert */}
          {isLocked && (
            <div className="bg-amber-50 border-2 border-amber-300/80 rounded-xl p-3.5 flex items-center justify-between gap-3 text-amber-950 shadow-sm animate-in fade-in duration-300">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-amber-200/90 rounded-xl text-amber-950 shrink-0">
                  <Lock className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold text-xs sm:text-sm text-amber-950 flex items-center gap-2">
                    <span>Kayıt Kilitlendi ({STATUS_LABELS[ticket.status]})</span>
                  </h4>
                  <p className="text-[11px] text-amber-800 mt-0.5 leading-relaxed">
                    Bu servis kaydı <strong>{STATUS_LABELS[ticket.status]}</strong> olarak kaydedildiği için form tamamen kilitlenmiştir. Kayıt üzerinde değişiklik ve düzenleme yapılamaz.
                  </p>
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-amber-200/70 border border-amber-400/50 rounded-lg text-xs font-bold text-amber-950 shrink-0 select-none">
                <Lock className="w-3.5 h-3.5" />
                <span>Kilitli Form</span>
              </div>
            </div>
          )}


          {/* Status Pipeline Step Buttons */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-blue-600" />
                Arıza & Servis İşlem Süreci Aşaması
              </span>
              <span className={`text-xs px-2.5 py-1 rounded-full font-bold border ${STATUS_COLORS[status].bg} ${STATUS_COLORS[status].text} ${STATUS_COLORS[status].border}`}>
                {STATUS_LABELS[status]}
              </span>
            </div>

            {/* If in Customer Approval Waiting status, show quick 1-click action buttons */}
            {status === 'MusteriOnayi' && (
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-300 rounded-xl p-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-xs">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-orange-500 text-white flex items-center justify-center font-bold text-base shrink-0 shadow-xs">
                    🔔
                  </div>
                  <div>
                    <div className="text-xs font-bold text-orange-950 flex items-center gap-1.5">
                      <span>Müşteri Fiyat / İşlem Onayı Bekleniyor</span>
                      <span className="text-[10px] bg-orange-200 text-orange-900 px-2 py-0.5 rounded-full font-bold">
                        İşlem Gerekli
                      </span>
                    </div>
                    <div className="text-[11px] text-orange-800 mt-0.5">
                      Müşteri görüşmesi sonucuna göre <b>[✓ Onaylandı]</b> veya <b>[✕ Onaylanmadı]</b> seçiniz:
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto shrink-0">
                  <button
                    type="button"
                    disabled={isLocked}
                    onClick={() => handleStatusChange('Onaylandi')}
                    className="flex-1 sm:flex-initial px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-sm flex items-center justify-center gap-1.5 transition-all hover:scale-[1.02] cursor-pointer"
                    title="Müşteri onay verdi"
                  >
                    <Check className="w-4 h-4" />
                    <span>✓ Onaylandı</span>
                  </button>
                  <button
                    type="button"
                    disabled={isLocked}
                    onClick={() => handleStatusChange('Onaylanmadi')}
                    className="flex-1 sm:flex-initial px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl shadow-sm flex items-center justify-center gap-1.5 transition-all hover:scale-[1.02] cursor-pointer"
                    title="Müşteri onay vermedi"
                  >
                    <X className="w-4 h-4" />
                    <span>✕ Onaylanmadı</span>
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-12 gap-1.5">
              {STATUS_ORDER.map(st => {
                const isActive = status === st;
                const isStatusRestricted =
                  isLimitedTechnician &&
                  st !== 'TeslimEdildi' &&
                  st !== 'IadeEdildi' &&
                  st !== 'Onaylandi' &&
                  st !== 'Onaylanmadi' &&
                  st !== 'MusteriOnayi';
                const isBtnDisabled = isLocked || isStatusRestricted;
                return (
                  <button
                    key={st}
                    disabled={isBtnDisabled}
                    onClick={() => handleStatusChange(st)}
                    title={
                      isLocked
                        ? 'Form kilitli'
                        : isStatusRestricted
                        ? 'Karşılama rolü sadece Onaylandı, Onaylanmadı, Onay Bekliyor, İade Edildi veya Teslim Edildi seçebilir'
                        : STATUS_LABELS[st]
                    }
                    className={`px-1.5 py-2 text-[11px] font-semibold rounded-lg border text-center transition-all whitespace-nowrap overflow-hidden text-ellipsis ${
                      isActive
                        ? 'bg-blue-600 text-white border-blue-600 shadow-md scale-[1.02]'
                        : isBtnDisabled
                        ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-50'
                        : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100 hover:border-slate-300 cursor-pointer'
                    }`}
                  >
                    {STATUS_LABELS[st].replace(' Müşteri', '').replace(' Teslim', '')}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Süreç & Personel Bilgileri (Kayıt Açan, Onaran, Teslim Eden) - Kompakt Liste */}
          <div className="bg-slate-50/80 border border-slate-200/90 rounded-xl p-3 shadow-xs">
            <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-200/70 text-[11px] font-bold text-slate-600 uppercase tracking-wider">
              <div className="flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-slate-500" />
                <span>Süreç & Personel Kayıtları</span>
              </div>
              <span className="text-[10px] text-slate-400 font-normal lowercase tracking-normal hidden sm:inline">
                kaydet ile otomatik güncellenir
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 text-xs">
              {/* 1. Kayıt Açan */}
              <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                    Kayıt Açan (Kabul)
                  </div>
                  <div className="font-bold text-slate-800 truncate text-[11px] mt-0.5 flex items-center gap-1">
                    <span>👤</span>
                    <span className="truncate">{ticket.createdByName || ticket.createdByUser || 'Belirtilmedi'}</span>
                  </div>
                </div>
                <div className="text-[10px] text-slate-400 font-mono shrink-0 text-right">
                  {ticket.createdAt
                    ? new Date(ticket.createdAt).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })
                    : '-'}
                </div>
              </div>

              {/* 2. Onaran / Teknisyen */}
              <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-semibold text-indigo-500 uppercase tracking-wider">
                    Onaran / Teknisyen
                  </div>
                  <div className="font-bold text-slate-800 truncate text-[11px] mt-0.5 flex items-center gap-1">
                    <span>👨‍🔧</span>
                    <span className="truncate">
                      {assignedTechnicianName || assignedTechnicianUsername || repairedByName || ticket.repairedByName || (
                        <span className="text-slate-400 font-normal italic">Atanmadı (Boşta)</span>
                      )}
                    </span>
                  </div>
                </div>

                {/* Quick actions in compact mode */}
                <div className="shrink-0 flex items-center gap-1.5">
                  {currentUser && (
                    <>
                      {currentUser.role === 'technician' && !isAssignedToCurrentUser && (
                        <button
                          type="button"
                          disabled={isLocked}
                          onClick={handleAssignToMe}
                          className={`text-[10px] font-bold px-2.5 py-1 rounded flex items-center gap-1 transition-all shadow-xs ${
                            isLocked
                              ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                              : assignedTechnicianName
                              ? 'bg-amber-600 hover:bg-amber-700 text-white cursor-pointer'
                              : 'bg-indigo-600 hover:bg-indigo-700 text-white cursor-pointer'
                          }`}
                          title={
                            assignedTechnicianName
                              ? `${assignedTechnicianName} üzerinden devral ve onarıma al`
                              : 'Bu formu üzerinize alıp onarıma başlatın'
                          }
                        >
                          <UserPlus className="w-2.5 h-2.5" />
                          <span>{assignedTechnicianName ? 'Devral (Onarıma Al)' : 'Kendine Ata (Onarıma Al)'}</span>
                        </button>
                      )}
                      {currentUser.role === 'technician' && isAssignedToCurrentUser && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                            Size Atandı
                          </span>
                          {!isLocked && (
                            <button
                              type="button"
                              onClick={handleUnassign}
                              className="text-[10px] text-red-500 hover:underline cursor-pointer"
                              title="Üzerinizden bırakıp boşa çıkarın"
                            >
                              Bırak
                            </button>
                          )}
                        </div>
                      )}
                      {currentUser.role === 'admin' && (
                        <select
                          disabled={isLocked}
                          value={assignedTechnicianId || (assignedTechnicianUsername ? `un_${assignedTechnicianUsername}` : '')}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (!val) {
                              setAssignedTechnicianId('');
                              setAssignedTechnicianName('');
                              setAssignedTechnicianUsername('');
                              const updated: ServiceTicket = {
                                ...ticket,
                                assignedTechnicianId: undefined,
                                assignedTechnicianName: undefined,
                                assignedTechnicianUsername: undefined,
                                updatedAt: new Date().toISOString(),
                              };
                              onSave(updated);
                            } else {
                              const selectedUser = allStoredUsers.find(u => u.id === val || `un_${u.username}` === val);
                              if (selectedUser) {
                                setAssignedTechnicianId(selectedUser.id);
                                setAssignedTechnicianName(selectedUser.name);
                                setAssignedTechnicianUsername(selectedUser.username);
                                setRepairedByName(selectedUser.name);
                                setRepairedByUser(selectedUser.username);
                                const updated: ServiceTicket = {
                                  ...ticket,
                                  assignedTechnicianId: selectedUser.id,
                                  assignedTechnicianName: selectedUser.name,
                                  assignedTechnicianUsername: selectedUser.username,
                                  repairedByName: selectedUser.name,
                                  repairedByUser: selectedUser.username,
                                  repairedAt: new Date().toISOString(),
                                  updatedAt: new Date().toISOString(),
                                };
                                onSave(updated);
                              }
                            }
                          }}
                          className={`bg-white border border-slate-200 text-[10px] font-semibold text-slate-800 rounded px-1.5 py-0.5 focus:outline-none max-w-[110px] truncate ${
                            isLocked ? 'cursor-not-allowed bg-slate-100 text-slate-400' : 'cursor-pointer'
                          }`}
                        >
                          <option value="">-- Boşta --</option>
                          {allStoredUsers.map(u => (
                            <option key={u.id} value={u.id}>
                              {u.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* 3. Teslim Eden / İade Eden */}
              <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider">
                      Teslim / İade Eden
                    </span>
                    <span className={`text-[9px] font-bold px-1 py-0.2 rounded ${
                      status === 'TeslimEdildi'
                        ? 'bg-emerald-100 text-emerald-800'
                        : status === 'IadeEdildi'
                        ? 'bg-rose-100 text-rose-800'
                        : 'bg-slate-100 text-slate-500'
                    }`}>
                      {status === 'TeslimEdildi' ? 'Teslim' : status === 'IadeEdildi' ? 'İade' : 'Bekliyor'}
                    </span>
                  </div>
                  <div className="font-bold text-slate-800 truncate text-[11px] mt-0.5 flex items-center gap-1">
                    <span>🚚</span>
                    <span className="truncate">
                      {deliveredByName || ticket.deliveredByName || (
                        status === 'TeslimEdildi' || status === 'IadeEdildi'
                          ? (currentUser?.name || 'Sistem Yöneticisi')
                          : <span className="text-slate-400 font-normal italic">Teslimde Belirlenir</span>
                      )}
                    </span>
                  </div>
                </div>
                <div className="text-[10px] text-slate-400 font-mono shrink-0 text-right">
                  {deliveredAt || ticket.deliveredAt || ticket.deliveryDate
                    ? new Date(deliveredAt || ticket.deliveredAt || ticket.deliveryDate!).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })
                    : '-'}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column: Customer & Device Details & Condition */}
            <div className="space-y-4">
              {/* Müşteri İletişim Bilgileri Bloğu */}
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-3">
                <div className="flex items-center justify-between border-b pb-2 border-slate-100">
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                    <User className="w-4 h-4 text-blue-600" />
                    Müşteri İletişim Bilgileri
                  </span>
                  {ticket.contractedCompanyId && (
                    <span className="text-[10px] bg-indigo-50 text-indigo-700 font-bold px-2 py-0.5 rounded-full border border-indigo-200 flex items-center gap-1">
                      <Building2 className="w-3 h-3" /> Kurumsal Cari
                    </span>
                  )}
                </div>

                <div className="p-3 bg-slate-50/80 rounded-xl border border-slate-200/80 space-y-2.5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <div>
                      <span className="text-[11px] font-semibold text-slate-400 block">Müşteri Adı Soyadı:</span>
                      <div className="text-sm font-bold text-slate-900 flex items-center gap-1.5 mt-0.5">
                        <span className="truncate">{ticket.customerName}</span>
                      </div>
                    </div>

                    <div>
                      <span className="text-[11px] font-semibold text-slate-400 block">Telefon Numarası:</span>
                      <div className="text-sm font-bold text-emerald-700 flex items-center gap-1.5 font-mono mt-0.5">
                        <Phone className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        <a href={`tel:${ticket.customerPhone}`} className="hover:underline">
                          {formatPhoneDisplay(ticket.customerPhone)}
                        </a>
                      </div>
                    </div>
                  </div>

                  {(ticket.customerEmail || ticket.customerAddress) && (
                    <div className="pt-2 border-t border-slate-200/70 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-600">
                      {ticket.customerEmail && (
                        <div className="flex items-center gap-1.5 truncate">
                          <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="truncate">{ticket.customerEmail}</span>
                        </div>
                      )}
                      {ticket.customerAddress && (
                        <div className="flex items-center gap-1.5 truncate">
                          <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="truncate">{ticket.customerAddress}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Cihaz Bilgileri & Durumu Bloğu */}
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-3">
                <h3 className="font-bold text-sm text-slate-800 border-b pb-2 border-slate-100 flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <Smartphone className="w-4 h-4 text-indigo-600" />
                    <span>Cihaz Bilgileri &amp; Durumu</span>
                  </span>
                  <span className="text-xs font-mono font-medium text-slate-500">
                    Seri: {ticket.serialNumber || 'Belirtilmedi'}
                  </span>
                </h3>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-slate-400 block">Cihaz / Marka / Model:</span>
                    {currentUser?.role === 'admin' ? (
                      <select
                        value={deviceType}
                        onChange={event => setDeviceType(event.target.value)}
                        className="mt-1 w-full rounded-md border border-indigo-200 bg-white px-2 py-1.5 text-xs font-semibold text-slate-800 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                        aria-label="Cihaz türünü düzenle"
                      >
                        {Array.from(new Set([ticket.deviceType, ...deviceTypes].filter((type): type is string => typeof type === 'string' && type.length > 0))).map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="font-semibold text-slate-800">{ticket.deviceType}</span>
                    )}
                    <span className="mt-0.5 block text-[11px] text-slate-500">{ticket.deviceBrand} {ticket.deviceModel}</span>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 block">Ekran / Kilit Şifresi:</span>
                      {canModifyDetails && (
                        <button
                          type="button"
                          onClick={() => setIsPatternModalOpen(true)}
                          className="text-[10px] font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 bg-indigo-50 hover:bg-indigo-100 px-1.5 py-0.5 rounded border border-indigo-200 cursor-pointer"
                          title="Desen Çiz / Düzenle"
                        >
                          <Grid3X3 className="w-2.5 h-2.5" />
                          <span>{isPatternPassword(devicePassword) ? 'Deseni Aç' : 'Desen Çiz'}</span>
                        </button>
                      )}
                    </div>
                    {!canModifyDetails ? (
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-slate-800">
                          {devicePassword || 'Kilit Yok / Belirtilmedi'}
                        </span>
                        {devicePassword && (
                          <PatternThumbnail patternStr={devicePassword} size={28} className="shrink-0" />
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <div className="relative flex-1">
                          <input
                            type="text"
                            value={devicePassword}
                            onChange={e => setDevicePassword(e.target.value)}
                            placeholder="Şifre veya desen"
                            className="w-full px-2 py-1 text-xs border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 font-medium"
                          />
                        </div>
                        {devicePassword && (
                          <div onClick={() => setIsPatternModalOpen(true)} className="cursor-pointer" title="Deseni büyüt / düzenle">
                            <PatternThumbnail patternStr={devicePassword} size={28} className="shrink-0 hover:ring-2 hover:ring-blue-500 transition-all" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="col-span-2">
                    <span className="text-slate-400 block mb-0.5">Garanti Durumu:</span>
                    <div className="flex items-center space-x-2">
                      <label className={`inline-flex items-center ${!canModifyDetails ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          disabled={!canModifyDetails}
                          checked={isUnderWarranty}
                          onChange={e => setIsUnderWarranty(e.target.checked)}
                          className={`rounded border-slate-300 text-blue-600 focus:ring-blue-500 ${!canModifyDetails ? 'cursor-not-allowed opacity-60' : ''}`}
                        />
                        <span className="ml-2 text-xs font-bold text-slate-700">
                          {isUnderWarranty ? (
                            <span className="text-emerald-600 flex items-center gap-1">
                              <ShieldCheck className="w-3.5 h-3.5" /> Garanti Kapsamında
                            </span>
                          ) : (
                            <span className="text-slate-500 flex items-center gap-1">
                              <ShieldAlert className="w-3.5 h-3.5 text-amber-500" /> Garanti Dışı
                            </span>
                          )}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="col-span-2">
                    <span className="text-slate-400 block">Fiziksel Durum:</span>
                    <span className="text-slate-700 bg-slate-50 p-2 rounded-lg border border-slate-100 block text-xs">
                      {ticket.physicalCondition || 'Gözle görülür hasar yok'}
                    </span>
                  </div>

                  <div className="col-span-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600 font-semibold text-xs flex items-center gap-1.5">
                        <Package className="w-3.5 h-3.5 text-blue-600" />
                        <span>Teslim Alınan Aksesuarlar ({includedAccessories.length}):</span>
                      </span>
                    </div>

                    {/* Aksesuar Badges Listesi */}
                    <div className="flex flex-wrap gap-1.5 min-h-[32px] p-2 bg-slate-50 border border-slate-200 rounded-lg">
                      {includedAccessories.length > 0 ? (
                        includedAccessories.map((acc, idx) => {
                          const isEditing = editingAccessoryIdx === idx;
                          if (isEditing && canModifyDetails) {
                            return (
                              <div key={idx} className="flex items-center bg-white border-2 border-blue-500 rounded-md p-0.5 shadow-sm">
                                <input
                                  type="text"
                                  autoFocus
                                  value={editingAccessoryText}
                                  onChange={e => setEditingAccessoryText(e.target.value)}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleSaveEditAccessory(idx);
                                    } else if (e.key === 'Escape') {
                                      setEditingAccessoryIdx(null);
                                    }
                                  }}
                                  className="text-xs px-1.5 py-0.5 border-0 focus:outline-none w-32 font-bold text-slate-900"
                                />
                                <button
                                  type="button"
                                  onClick={() => handleSaveEditAccessory(idx)}
                                  className="p-0.5 text-emerald-600 hover:bg-emerald-50 rounded cursor-pointer"
                                  title="Kaydet"
                                >
                                  <Check className="w-3 h-3" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingAccessoryIdx(null)}
                                  className="p-0.5 text-slate-400 hover:bg-slate-100 rounded cursor-pointer"
                                  title="İptal"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            );
                          }

                          return (
                            <span
                              key={idx}
                              className="bg-white text-slate-800 text-[11px] font-semibold px-2 py-1 rounded-md border border-slate-200 shadow-xs flex items-center gap-1 group hover:border-blue-300"
                            >
                              <span>{acc}</span>
                              {canModifyDetails && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handleStartEditAccessory(idx, acc)}
                                    title="İsmi Düzelt"
                                    className="p-0.5 text-slate-400 hover:text-blue-600 rounded transition-colors cursor-pointer"
                                  >
                                    <Edit3 className="w-3 h-3" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveAccessory(acc)}
                                    title="Kaldır"
                                    className="p-0.5 text-slate-400 hover:text-rose-600 rounded transition-colors cursor-pointer"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </>
                              )}
                            </span>
                          );
                        })
                      ) : (
                        <span className="text-slate-400 italic text-xs self-center">Aksesuar teslim alınmadı.</span>
                      )}
                    </div>

                    {/* Hızlı Aksesuar Ekleme Girişi (Yetkili ise göster) */}
                    {canModifyDetails && (
                      <div className="flex gap-1.5">
                        <input
                          type="text"
                          placeholder="Aksesuar ekle..."
                          value={newAccessoryInput}
                          onChange={e => setNewAccessoryInput(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddAccessory();
                            }
                          }}
                          className="flex-1 px-2.5 py-1 text-xs border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                        />
                        <button
                          type="button"
                          onClick={handleAddAccessory}
                          disabled={!newAccessoryInput.trim()}
                          className="px-2.5 py-1 bg-slate-800 hover:bg-slate-900 disabled:bg-slate-200 disabled:text-slate-400 text-white text-xs font-semibold rounded-md flex items-center gap-1 transition-colors cursor-pointer disabled:cursor-not-allowed"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          <span>Ekle</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Customer Complaint */}
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-2">
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Müşteri Şikayeti & Arıza Tanımı:
                </h4>
                <p className="text-xs text-slate-800 bg-amber-50/60 p-3 rounded-lg border border-amber-200/60 leading-relaxed font-medium">
                  {ticket.issueDescription}
                </p>
              </div>

              {/* Past Service History for Same Device & Customer */}
              {pastDeviceTickets.length > 0 ? (
                <div className="bg-gradient-to-br from-indigo-50/90 via-blue-50/50 to-white p-4 rounded-xl border-2 border-indigo-200/80 shadow-sm space-y-3 animate-in fade-in duration-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="p-1.5 bg-indigo-600 text-white rounded-lg shadow-xs">
                        <History className="w-4 h-4" />
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-indigo-950 uppercase tracking-wider flex items-center gap-1.5">
                          <span>Bu Cihazın Geçmiş Servis Kayıtları</span>
                          <span className="bg-indigo-600 text-white text-[10px] px-1.5 py-0.2 rounded-full font-bold">
                            {pastDeviceTickets.length} Kayıt
                          </span>
                        </h4>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setIsPastHistoryOpen(!isPastHistoryOpen)}
                      className="text-indigo-600 hover:text-indigo-800 text-xs font-bold flex items-center space-x-1 p-1 rounded hover:bg-indigo-100/60 transition-colors cursor-pointer"
                    >
                      <span>{isPastHistoryOpen ? 'Gizle' : 'Göster'}</span>
                      {isPastHistoryOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>

                  {isPastHistoryOpen && (
                    <div className="space-y-2.5 pt-1">
                      {pastDeviceTickets.map(pTicket => {
                        const pTotal = getTicketGrandTotal(pTicket);
                        const pBreakdown = getTicketContractBreakdown(pTicket);
                        const pDebt = pBreakdown.effectiveDebt;
                        const isWarrantyValid =
                          pTicket.warrantyDays &&
                          pTicket.warrantyDays > 0 &&
                          pTicket.deliveryDate
                            ? new Date(pTicket.deliveryDate).getTime() +
                                pTicket.warrantyDays * 86400000 >
                              Date.now()
                            : false;

                        return (
                          <div
                            key={pTicket.id}
                            className="bg-white border border-indigo-100 rounded-lg p-3 shadow-xs hover:border-indigo-300 transition-all space-y-2"
                          >
                            <div className="flex flex-wrap items-center justify-between gap-1.5 border-b border-slate-100 pb-2">
                              <div className="flex items-center space-x-2">
                                <span className="font-mono font-bold text-xs text-indigo-900 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-200">
                                  #{pTicket.id}
                                </span>
                                <span
                                  className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_COLORS[pTicket.status].bg} ${STATUS_COLORS[pTicket.status].text} ${STATUS_COLORS[pTicket.status].border}`}
                                >
                                  {STATUS_LABELS[pTicket.status]}
                                </span>
                              </div>

                              <div className="text-[11px] text-slate-500 flex items-center space-x-2">
                                <span>
                                  Kayıt: <strong>{formatDateTR(pTicket.createdAt)}</strong>
                                </span>
                                {pTicket.deliveryDate && (
                                  <span>
                                    • Teslim: <strong>{formatDateTR(pTicket.deliveryDate)}</strong>
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-1 gap-1.5 text-xs text-slate-700">
                              <div className="flex items-start gap-1">
                                <span className="font-bold text-slate-900 shrink-0">Arıza/Şikayet:</span>
                                <span className="text-slate-700">{pTicket.issueDescription}</span>
                              </div>

                              {pTicket.technicianNotes && (
                                <div className="flex items-start gap-1 bg-slate-50 p-1.5 rounded border border-slate-200/60">
                                  <span className="font-bold text-slate-900 shrink-0">Yapılan İşlem:</span>
                                  <span className="text-slate-600">{pTicket.technicianNotes}</span>
                                </div>
                              )}

                              {pTicket.parts && pTicket.parts.length > 0 && (
                                <div className="flex items-start gap-1 text-[11px]">
                                  <span className="font-bold text-slate-800 shrink-0">Değişen Parçalar:</span>
                                  <span className="text-slate-600">
                                    {pTicket.parts.map(p => `${p.name} (${p.quantity}x)`).join(', ')}
                                  </span>
                                </div>
                              )}

                              {/* Printer specific past info if present */}
                              {(pTicket.printerTotalPages !== undefined || pTicket.printerFirstPrintDate || pTicket.printerCleaningCount || pTicket.printerWasteBoxInfo) && (
                                <div className="bg-indigo-50/70 p-2 rounded-lg border border-indigo-200/80 text-[11px] text-slate-800 flex flex-wrap items-center gap-1.5 mt-1">
                                  <span className="font-bold text-indigo-950 flex items-center gap-1">
                                    <Printer className="w-3.5 h-3.5 text-indigo-600" />
                                    Yazıcı Sayaç/Bakım:
                                  </span>
                                  {pTicket.printerFirstPrintDate && (
                                    <span className="bg-white px-2 py-0.5 rounded border border-indigo-200 font-mono text-[10px] text-indigo-900 font-bold">
                                      İlk Çıktı: {pTicket.printerFirstPrintDate}
                                    </span>
                                  )}
                                  {pTicket.printerTotalPages !== undefined && pTicket.printerTotalPages !== '' && (
                                    <span className="bg-white px-2 py-0.5 rounded border border-indigo-200 font-mono text-[10px] text-indigo-900 font-bold">
                                      Sayaç: {Number(pTicket.printerTotalPages).toLocaleString('tr-TR')} Sayfa
                                    </span>
                                  )}
                                  {pTicket.printerCleaningCount && (
                                    <span className="bg-white px-2 py-0.5 rounded border border-indigo-200 text-[10px] text-slate-700">
                                      Temizleme: {pTicket.printerCleaningCount}
                                    </span>
                                  )}
                                  {pTicket.printerWasteBoxInfo && (
                                    <span className="bg-white px-2 py-0.5 rounded border border-indigo-200 text-[10px] text-slate-700">
                                      Atık Kutu: {pTicket.printerWasteBoxInfo}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>

                            <div className="flex flex-wrap items-center justify-between gap-2 pt-1.5 border-t border-slate-100 text-xs">
                              <div className="flex items-center space-x-2">
                                {pDebt > 0 ? (
                                  <span className="font-bold text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded text-xs">
                                    Borç: {formatTL(pDebt)}
                                  </span>
                                ) : (
                                  <span className="font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded text-xs">
                                    Borç Yok
                                  </span>
                                )}
                                {pTicket.warrantyDays !== undefined && pTicket.warrantyDays > 0 && (
                                  <span
                                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                      isWarrantyValid
                                        ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                        : 'bg-slate-100 text-slate-600'
                                    }`}
                                  >
                                    {isWarrantyValid
                                      ? `✓ ${pTicket.warrantyDays} Gün Garanti (Aktif)`
                                      : `${pTicket.warrantyDays} Gün Garanti`}
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center space-x-1.5">
                                {onSelectTicket && (
                                  <button
                                    type="button"
                                    onClick={() => onSelectTicket(pTicket)}
                                    className="px-2 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-800 border border-indigo-200 rounded text-[11px] font-bold flex items-center space-x-1 transition-colors cursor-pointer"
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                    <span>Kayda Git</span>
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => onPrintInvoice(pTicket)}
                                  className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-[11px] font-medium flex items-center space-x-1 transition-colors cursor-pointer"
                                  title="Bu kaydın faturasını yazdır"
                                >
                                  <Printer className="w-3 h-3 text-slate-500" />
                                  <span>Fatura</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3 text-xs text-slate-500 flex items-center gap-2">
                  <History className="w-4 h-4 text-slate-400 shrink-0" />
                  <span>Bu cihazın sistemde daha önce kayıtlı geçmiş servis kaydı bulunmamaktadır (İlk Servis Kaydı).</span>
                </div>
              )}

              {/* Printer Service & Metrics Card (Yazıcı Servis, Sayaç, Temizleme ve Atık Kutu Bilgileri) */}
              <div className={`p-4 rounded-xl border shadow-sm space-y-3.5 transition-all ${
                isPrinterDevice || printerTotalPages !== '' || printerCleaningCount !== '' || printerFirstPrintDate || printerWasteBoxInfo
                  ? 'bg-slate-50/50 border-indigo-200/90 ring-1 ring-indigo-50'
                  : 'bg-white border-slate-200'
              }`}>
                <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-2.5 border-slate-200/80">
                  <div className="flex items-center space-x-2.5">
                    <div className="p-1.5 bg-indigo-600 text-white rounded-lg shadow-xs shrink-0">
                      <Printer className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-xs font-extrabold text-slate-900 uppercase tracking-wider">
                          Yazıcı Servis &amp; Sayaç Bilgileri
                        </h4>
                        {isPrinterDevice && (
                          <span className="bg-indigo-100 text-indigo-800 text-[10px] px-2 py-0.5 rounded-full font-bold border border-indigo-200">
                            Yazıcı / Tarayıcı
                          </span>
                        )}
                        {previousPrinterTicket && (
                          <span className="hidden sm:inline-flex items-center gap-1 bg-blue-50 text-blue-800 text-[10px] px-2 py-0.5 rounded-full font-bold border border-blue-200">
                            <History className="w-3 h-3 text-blue-600" />
                            Önceki Kayıt: #{previousPrinterTicket.id} ({formatDateTR(previousPrinterTicket.createdAt)})
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        İlk çıktı tarihi, temizleme adetleri, sayaç ve atık kutusu takibi
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => setIsPrinterSectionOpen(!isPrinterSectionOpen)}
                      className="text-slate-500 hover:text-slate-700 text-xs font-bold flex items-center p-1 rounded-md hover:bg-slate-100 transition-colors cursor-pointer"
                    >
                      {isPrinterSectionOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {isPrinterSectionOpen && (
                  <div className="space-y-3">
                    {/* 4 Inputs Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      {/* 1. İlk Çıktı Tarihi */}
                      <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-2xs space-y-1.5 flex flex-col justify-between">
                        <div>
                          <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5 mb-1.5">
                            <Calendar className="w-3.5 h-3.5 text-indigo-600" />
                            İlk Çıktı / Kurulum Tarihi
                          </label>
                          <input
                            type="date"
                            disabled={!canModifyDetails}
                            value={printerFirstPrintDate}
                            onChange={e => setPrinterFirstPrintDate(e.target.value)}
                            className={`w-full text-xs p-2 h-9 border rounded-lg focus:outline-none transition-colors ${
                              !canModifyDetails
                                ? 'bg-slate-100 text-slate-700 border-slate-200 cursor-not-allowed'
                                : 'bg-white text-slate-900 border-slate-300 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500'
                            }`}
                          />
                        </div>

                        {/* Previous Service Value */}
                        {previousPrinterTicket?.printerFirstPrintDate && (
                          <div className="mt-1 pt-1.5 border-t border-slate-100 flex items-center justify-between text-[11px]">
                            <div className="truncate min-w-0">
                              <span className="text-slate-400 text-[10px] block">Önceki:</span>
                              <span className="font-mono text-indigo-900 font-bold">{previousPrinterTicket.printerFirstPrintDate}</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 2. Yapılan Temizleme */}
                      <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-2xs space-y-1.5 flex flex-col justify-between">
                        <div>
                          <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5 mb-1.5">
                            <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                            Yapılan Temizleme
                          </label>
                          <input
                            type="text"
                            disabled={!canModifyDetails}
                            value={printerCleaningCount}
                            onChange={e => setPrinterCleaningCount(e.target.value)}
                            placeholder="Örn: 2 Kafa, 1 Derin Temizleme"
                            className={`w-full text-xs p-2 h-9 border rounded-lg focus:outline-none transition-colors ${
                              !canModifyDetails
                                ? 'bg-slate-100 text-slate-700 border-slate-200 cursor-not-allowed'
                                : 'bg-white text-slate-900 border-slate-300 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500'
                            }`}
                          />
                        </div>

                        {/* Previous Service Value & Cleaning Diff */}
                        {previousPrinterTicket?.printerCleaningCount && (
                          <div className="mt-1 pt-1.5 border-t border-slate-100 space-y-1">
                            <div className="flex items-center justify-between text-[11px]">
                              <div className="truncate min-w-0">
                                <span className="text-slate-400 text-[10px] block">Önceki:</span>
                                <span className="font-mono text-indigo-900 font-bold truncate block">{previousPrinterTicket.printerCleaningCount}</span>
                              </div>
                            </div>
                            {printerCleaningDifference !== null && (
                              <div className="flex items-center justify-between text-[10px] bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">
                                <span className="text-slate-500">Fark:</span>
                                {printerCleaningDifference > 0 ? (
                                  <span className="font-bold text-emerald-700">+{printerCleaningDifference.toLocaleString('tr-TR')} adet</span>
                                ) : printerCleaningDifference === 0 ? (
                                  <span className="font-medium text-slate-500">0 adet</span>
                                ) : (
                                  <span className="font-bold text-amber-700">{printerCleaningDifference.toLocaleString('tr-TR')} adet</span>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* 3. Toplam Çıktı / Baskı Sayacı */}
                      <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-2xs space-y-1.5 flex flex-col justify-between">
                        <div>
                          <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5 mb-1.5">
                            <Gauge className="w-3.5 h-3.5 text-indigo-600" />
                            Toplam Çıktı (Sayaç)
                          </label>
                          <input
                            type="text"
                            disabled={!canModifyDetails}
                            value={printerTotalPages}
                            onChange={e => setPrinterTotalPages(e.target.value)}
                            placeholder="Örn: 18500 veya 18.500"
                            className={`w-full text-xs p-2 h-9 border rounded-lg focus:outline-none transition-colors ${
                              !canModifyDetails
                                ? 'bg-slate-100 text-slate-700 border-slate-200 cursor-not-allowed'
                                : 'bg-white text-slate-900 border-slate-300 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500'
                            }`}
                          />
                        </div>

                        {/* Previous Service Value & Diff */}
                        {previousPrinterTicket?.printerTotalPages !== undefined && previousPrinterTicket.printerTotalPages !== '' && (
                          <div className="mt-1 pt-1.5 border-t border-slate-100 space-y-1">
                            <div className="flex items-center justify-between text-[11px]">
                              <div className="truncate min-w-0">
                                <span className="text-slate-400 text-[10px] block">Önceki:</span>
                                <span className="font-mono text-indigo-900 font-bold">{Number(previousPrinterTicket.printerTotalPages).toLocaleString('tr-TR')}</span>
                              </div>
                            </div>
                            {printerPageDifference !== null && (
                              <div className="flex items-center justify-between text-[10px] bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">
                                <span className="text-slate-500">Fark:</span>
                                {printerPageDifference > 0 ? (
                                  <span className="font-bold text-emerald-700">+{printerPageDifference.toLocaleString('tr-TR')} sf</span>
                                ) : printerPageDifference === 0 ? (
                                  <span className="font-medium text-slate-500">0 sf</span>
                                ) : (
                                  <span className="font-bold text-amber-700">{printerPageDifference.toLocaleString('tr-TR')} sf</span>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* 4. Atık Kutu Bilgileri */}
                      <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-2xs space-y-1.5 flex flex-col justify-between">
                        <div>
                          <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5 mb-1.5">
                            <Package className="w-3.5 h-3.5 text-indigo-600" />
                            Atık Kutu / Keçe Bilgisi
                          </label>
                          <input
                            type="text"
                            disabled={!canModifyDetails}
                            value={printerWasteBoxInfo}
                            onChange={e => setPrinterWasteBoxInfo(e.target.value)}
                            placeholder="Örn: %85 Dolu, Sıfırlandı"
                            className={`w-full text-xs p-2 h-9 border rounded-lg focus:outline-none transition-colors ${
                              !canModifyDetails
                                ? 'bg-slate-100 text-slate-700 border-slate-200 cursor-not-allowed'
                                : 'bg-white text-slate-900 border-slate-300 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500'
                            }`}
                          />
                        </div>

                        {/* Previous Service Value */}
                        {previousPrinterTicket?.printerWasteBoxInfo && (
                          <div className="mt-1 pt-1.5 border-t border-slate-100 flex items-center justify-between text-[11px]">
                            <div className="truncate min-w-0">
                              <span className="text-slate-400 text-[10px] block">Önceki:</span>
                              <span className="font-mono text-indigo-900 font-bold truncate block">{previousPrinterTicket.printerWasteBoxInfo}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Performed Actions / Repair Description */}
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-2.5">
                <div className="flex items-center justify-between border-b pb-2 border-slate-100">
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                    <Wrench className="w-4 h-4 text-blue-600" />
                    Yapılan İşlem / Onarım Açıklaması
                  </h4>
                </div>
                <textarea
                  rows={3}
                  disabled={!canModifyDetails}
                  readOnly={!canModifyDetails}
                  value={performedActions}
                  onChange={e => setPerformedActions(e.target.value)}
                  placeholder={!canModifyDetails ? (isLimitedTechnician ? 'Karşılama: Onarım açıklaması düzenlenemez.' : 'Kayıt kilitli.') : 'Yapılan işlem ve onarım açıklaması...'}
                  className={`w-full text-xs p-3 border rounded-lg focus:outline-none ${
                    !canModifyDetails
                      ? 'bg-slate-100 text-slate-700 border-slate-200 cursor-not-allowed select-text'
                      : 'bg-white text-slate-900 border-slate-300 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500'
                  }`}
                />
              </div>

              {ticket.serviceType === 'DisServis' ? (
                <div className="p-4 rounded-xl border border-cyan-200 bg-cyan-50/50 shadow-sm space-y-2">
                  <h4 className="text-xs font-bold text-cyan-900 uppercase tracking-wider">Şehir Dışı Servis Gideri</h4>
                  <p className="text-[11px] text-cyan-800">Yerinde servis için yapılan yakıt, yemek ve benzeri giderlerin toplamını girin.</p>
                  <div className="max-w-xs">
                    <label className="block text-slate-700 font-semibold mb-1">Şehir Dışı Servis Giderleri (₺)</label>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      disabled={!canModifyDetails}
                      readOnly={!canModifyDetails}
                      value={outsideServiceCost || ''}
                      onChange={event => setOutsideServiceCost(Math.max(0, parseFloat(event.target.value) || 0))}
                      placeholder="0"
                      className={`w-full px-3 py-2 border rounded-lg text-sm font-bold font-mono focus:outline-none ${!canModifyDetails ? 'bg-slate-100 text-slate-700 border-slate-200 cursor-not-allowed' : 'bg-white text-cyan-900 border-cyan-300 focus:ring-2 focus:ring-cyan-500/20'}`}
                    />
                  </div>
                </div>
              ) : (
              <>
              {/* Sub-Service (Alt Servis) Card */}
              <div className={`p-4 rounded-xl border shadow-sm space-y-3 transition-all ${
                status === 'AltServiste' || subServiceCost > 0 || subServiceName
                  ? 'bg-cyan-50/40 border-cyan-300 ring-1 ring-cyan-200'
                  : 'bg-white border-slate-200'
              }`}>
                <div className="flex items-center justify-between border-b pb-2 border-slate-200/80">
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                    <Building2 className="w-4 h-4 text-cyan-600" />
                    Alt Servis / Dış Onarım & Maliyeti
                  </h4>
                  {status === 'AltServiste' ? (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-cyan-100 text-cyan-800 border border-cyan-300 flex items-center gap-1">
                      <Truck className="w-3 h-3" />
                      Alt Serviste
                    </span>
                  ) : canModifyDetails && (
                    <button
                      type="button"
                      onClick={() => handleStatusChange('AltServiste')}
                      className="text-[11px] font-bold text-cyan-700 hover:text-cyan-800 hover:underline flex items-center gap-1 cursor-pointer"
                      title="Durumu 'Alt Serviste' olarak değiştirir"
                    >
                      <Layers className="w-3 h-3" />
                      <span>Alt Servise Gönder</span>
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="relative">
                    <label className="block text-slate-700 font-semibold mb-1">
                      Alt Servis / Firma Adı
                    </label>
                    <div className="relative">
                      <Building2 className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5 pointer-events-none" />
                      <input
                        type="text"
                        disabled={!canModifyDetails}
                        readOnly={!canModifyDetails}
                        value={subServiceName}
                        onChange={e => {
                          setSubServiceName(e.target.value);
                          setShowSubServiceSuggestions(true);
                        }}
                        onFocus={() => {
                          if (canModifyDetails) setShowSubServiceSuggestions(true);
                        }}
                        onBlur={() => setTimeout(() => setShowSubServiceSuggestions(false), 200)}
                        placeholder={!canModifyDetails ? '-' : 'Alt servis firma adı'}
                        className={`w-full pl-8 pr-3 py-1.5 border rounded-lg text-xs font-medium focus:outline-none ${
                          !canModifyDetails
                            ? 'bg-slate-100 text-slate-700 border-slate-200 cursor-not-allowed'
                            : 'bg-white text-slate-900 border-slate-300 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500'
                        }`}
                      />
                    </div>

                    {/* Live Sub-Service Autocomplete Suggestions Dropdown */}
                    {showSubServiceSuggestions && matchingSubServices.length > 0 && canModifyDetails && (
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-cyan-200 shadow-xl rounded-xl z-30 max-h-56 overflow-y-auto divide-y divide-slate-100 animate-in fade-in zoom-in-95 duration-150">
                        <div className="px-3 py-1.5 bg-cyan-50/90 text-[11px] font-bold text-cyan-900 flex items-center justify-between border-b border-cyan-100">
                          <span className="flex items-center space-x-1">
                            <Building2 className="w-3.5 h-3.5 text-cyan-600" />
                            <span>Kayıtlı Alt Servis Carileri ({matchingSubServices.length})</span>
                          </span>
                          <span className="text-[10px] font-normal text-slate-500">Tıklayıp Seçin</span>
                        </div>
                        {matchingSubServices.map((sub, idx) => (
                          <div
                            key={idx}
                            onMouseDown={e => {
                              e.preventDefault();
                              handleSelectSubService(sub);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-cyan-50/80 transition-colors flex items-center justify-between text-xs group cursor-pointer"
                          >
                            <div className="flex-1 pr-2">
                              <div className="font-bold text-slate-900 group-hover:text-cyan-700 flex items-center space-x-1.5">
                                <span>{sub.name}</span>
                                <span className="text-[10px] bg-cyan-100 text-cyan-800 font-semibold px-1.5 py-0.5 rounded">
                                  {sub.ticketCount} Cihaz
                                </span>
                              </div>
                              <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-2">
                                <span>Toplam İşlem: {formatTL(sub.totalSpent)}</span>
                                {sub.lastUsedCost && sub.lastUsedCost > 0 ? (
                                  <span>• Son Ücret: {formatTL(sub.lastUsedCost)}</span>
                                ) : null}
                              </div>
                            </div>

                            <div className="text-right shrink-0">
                              {sub.unpaidDebt > 0 ? (
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-800 border border-rose-200 flex items-center gap-0.5">
                                  <AlertCircle className="w-3 h-3 text-rose-600" />
                                  Borç: {formatTL(sub.unpaidDebt)}
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200 flex items-center gap-0.5">
                                  <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                                  Bakiye Temiz
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">
                      Alt Servis Maliyeti (₺)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      disabled={!canModifyDetails}
                      readOnly={!canModifyDetails}
                      value={subServiceCost || ''}
                      onChange={e => setSubServiceCost(parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      className={`w-full px-3 py-1.5 border rounded-lg text-xs font-bold font-mono focus:outline-none ${
                        !canModifyDetails
                          ? 'bg-slate-100 text-slate-700 border-slate-200 cursor-not-allowed'
                          : 'bg-white text-cyan-900 border-cyan-300 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500'
                      }`}
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">
                      Takip / İrsaliye / Fiş No
                    </label>
                    <input
                      type="text"
                      disabled={!canModifyDetails}
                      readOnly={!canModifyDetails}
                      value={subServiceTrackingNo}
                      onChange={e => setSubServiceTrackingNo(e.target.value)}
                      placeholder={!canModifyDetails ? '-' : 'İrsaliye / Fiş No'}
                      className={`w-full px-3 py-1.5 border rounded-lg text-xs font-medium focus:outline-none ${
                        !canModifyDetails
                          ? 'bg-slate-100 text-slate-700 border-slate-200 cursor-not-allowed'
                          : 'bg-white text-slate-900 border-slate-300 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500'
                      }`}
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">
                      Alt Servis Açıklama / Not
                    </label>
                    <input
                      type="text"
                      disabled={!canModifyDetails}
                      readOnly={!canModifyDetails}
                      value={subServiceNotes}
                      onChange={e => setSubServiceNotes(e.target.value)}
                      placeholder={!canModifyDetails ? '-' : 'İşlem notu'}
                      className={`w-full px-3 py-1.5 border rounded-lg text-xs font-medium focus:outline-none ${
                        !canModifyDetails
                          ? 'bg-slate-100 text-slate-700 border-slate-200 cursor-not-allowed'
                          : 'bg-white text-slate-900 border-slate-300 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500'
                      }`}
                    />
                  </div>
                </div>

                {/* Sub-Service Cari & Payment Status Section */}
                {(subServiceCost > 0 || subServiceName || status === 'AltServiste') && (
                  <div className="pt-3 border-t border-cyan-200/80 bg-white/80 p-3.5 rounded-lg border border-cyan-100 space-y-3">
                    {(() => {
                      const effPaid =
                        subServicePaymentStatus === 'Odendi'
                          ? subServiceCost
                          : subServicePaymentStatus === 'KismiOdendi'
                          ? subServicePaidAmount
                          : 0;
                      const effRemaining = Math.max(0, subServiceCost - effPaid);

                      return (
                        <>
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-slate-800">Alt Servis Cari Ödeme Durumu:</span>
                              {subServicePaymentStatus === 'Odendi' ? (
                                <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 flex items-center gap-1">
                                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                  Ödendi {subServiceCost > 0 ? `(${formatTL(subServiceCost)})` : ''}
                                </span>
                              ) : subServicePaymentStatus === 'KismiOdendi' ? (
                                <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-amber-100 text-amber-900 border border-amber-300 flex items-center gap-1">
                                  <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                                  Kısmi Ödendi ({formatTL(effPaid)} / {formatTL(subServiceCost)}) • Kalan: {formatTL(effRemaining)}
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-rose-100 text-rose-800 border border-rose-300 flex items-center gap-1">
                                  <AlertCircle className="w-3.5 h-3.5 text-rose-600" />
                                  Ödenmedi - Cari Borç {subServiceCost > 0 ? `(${formatTL(subServiceCost)})` : ''}
                                </span>
                              )}
                            </div>

                            {canModifyDetails && (
                              <div className="flex items-center gap-1 bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSubServicePaymentStatus('Odenmedi');
                                    setSubServicePaidAmount(0);
                                    setSubServicePaidDate('');
                                  }}
                                  className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                                    subServicePaymentStatus === 'Odenmedi'
                                      ? 'bg-rose-600 text-white shadow-xs'
                                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                                  }`}
                                >
                                  Ödenmedi (Borç)
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSubServicePaymentStatus('KismiOdendi');
                                    if (subServicePaidAmount <= 0) {
                                      setSubServicePaidAmount(Math.round(subServiceCost / 2));
                                    }
                                    if (!subServicePaidDate) setSubServicePaidDate(new Date().toISOString());
                                  }}
                                  className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                                    subServicePaymentStatus === 'KismiOdendi'
                                      ? 'bg-amber-600 text-white shadow-xs'
                                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                                  }`}
                                >
                                  Kısmi Ödeme
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSubServicePaymentStatus('Odendi');
                                    setSubServicePaidAmount(subServiceCost);
                                    if (!subServicePaidDate) setSubServicePaidDate(new Date().toISOString());
                                  }}
                                  className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                                    subServicePaymentStatus === 'Odendi'
                                      ? 'bg-emerald-600 text-white shadow-xs'
                                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                                  }`}
                                >
                                  Tam Ödendi
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Partial payment amount input */}
                          {subServicePaymentStatus === 'KismiOdendi' && (
                            <div className="bg-amber-50/80 border border-amber-200 p-2.5 rounded-lg grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                              <div>
                                <label className="block text-amber-900 font-bold mb-1">
                                  Alt Servise Ödenen Tutar (₺)
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  max={subServiceCost || 999999}
                                  step="any"
                                  disabled={isLocked}
                                  value={subServicePaidAmount || ''}
                                  onChange={e => {
                                    const val = parseFloat(e.target.value) || 0;
                                    setSubServicePaidAmount(val);
                                    if (val >= subServiceCost && subServiceCost > 0) {
                                      setSubServicePaymentStatus('Odendi');
                                    }
                                  }}
                                  placeholder="0"
                                  className="w-full px-2.5 py-1.5 border border-amber-300 rounded-md text-xs font-bold font-mono bg-white text-slate-900 focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500"
                                />
                              </div>
                              <div className="flex flex-col justify-center bg-white/80 border border-amber-100 p-2 rounded-md">
                                <span className="text-[11px] text-slate-500 font-medium">Kalan Alt Servis Borcu:</span>
                                <span className="text-sm font-black font-mono text-rose-600">
                                  {formatTL(effRemaining)}
                                </span>
                              </div>
                            </div>
                          )}

                          {(subServicePaymentStatus === 'Odendi' || subServicePaymentStatus === 'KismiOdendi') && (
                            <div className="pt-2 border-t border-slate-100 text-xs">
                              <label className="block text-slate-600 font-medium mb-1">
                                Ödeme Notu
                              </label>
                              <input
                                type="text"
                                disabled={isLocked}
                                value={subServicePaidNote}
                                onChange={e => setSubServicePaidNote(e.target.value)}
                                placeholder="Ödeme notu..."
                                className="w-full px-2.5 py-1.5 border border-slate-300 rounded-md text-xs bg-white text-slate-800 focus:ring-1 focus:ring-emerald-500"
                              />
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
              </>
              )}

              {/* Service & Delivery Dates */}
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-3">
                <div className="flex items-center justify-between border-b pb-2 border-slate-100">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-blue-600" />
                    Servis & Teslim Tarihleri
                  </h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">
                      Cihaz Geliş / Kayıt Tarihi
                    </label>
                    <div className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs font-bold text-slate-600 bg-slate-100 cursor-not-allowed select-none">
                      {formatDateTR(ticket.createdAt)}
                    </div>
                  </div>

                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">
                      Cihaz Teslim / İşlem Tarihi
                    </label>
                    <input
                      type="datetime-local"
                      disabled={!canModifyDetails}
                      readOnly={!canModifyDetails}
                      value={deliveryDateInput}
                      onChange={e => setDeliveryDateInput(e.target.value)}
                      className={`w-full px-2.5 py-1.5 border rounded-lg text-xs font-medium focus:outline-none ${
                        !canModifyDetails
                          ? 'bg-slate-100 text-slate-700 border-slate-200 cursor-not-allowed'
                          : 'bg-white text-slate-900 border-slate-300 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500'
                      }`}
                    />
                  </div>
                </div>

                {/* Servis Garanti Süresi Düzenleme */}
                <div className="pt-2.5 border-t border-slate-100 space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                      <ShieldCheck className="w-4 h-4 text-emerald-600" />
                      <span>Tamir / Servis Garanti Süresi:</span>
                    </label>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="number"
                        min="0"
                        max="3650"
                        disabled={!canModifyDetails}
                        readOnly={!canModifyDetails}
                        value={warrantyDays === 0 ? '' : warrantyDays}
                        placeholder="0"
                        onChange={e => {
                          const val = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                          setWarrantyDays(isNaN(val) ? 0 : Math.max(0, val));
                        }}
                        className={`w-20 px-2 py-1 border rounded-lg text-xs font-bold text-center focus:outline-none ${
                          !canModifyDetails
                            ? 'bg-slate-100 text-slate-700 border-slate-200 cursor-not-allowed'
                            : 'bg-white text-slate-900 border-slate-300 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500'
                        }`}
                      />
                      <span className="text-xs font-bold text-slate-600">Gün</span>
                    </div>
                  </div>

                  {/* Hızlı Seçim Butonları */}
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { label: 'Garantisiz', days: 0 },
                      { label: '30 Gün', days: 30 },
                      { label: '60 Gün', days: 60 },
                      { label: '90 Gün', days: 90 },
                      { label: '180 Gün (6 Ay)', days: 180 },
                      { label: '365 Gün (1 Yıl)', days: 365 },
                    ].map(opt => (
                      <button
                        key={opt.days}
                        type="button"
                        disabled={!canModifyDetails}
                        onClick={() => setWarrantyDays(opt.days)}
                        className={`text-[11px] px-2.5 py-1 rounded-md font-medium transition-all ${
                          warrantyDays === opt.days
                            ? 'bg-emerald-600 text-white font-bold shadow-sm ring-1 ring-emerald-700'
                            : !canModifyDetails
                            ? 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed opacity-60'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200 cursor-pointer'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Spare Parts, Labor & Billing */}
            <div className="space-y-4">
              {/* Spare Parts List */}
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-3">
                <h3 className="font-bold text-sm text-slate-800 border-b pb-2 border-slate-100 flex items-center justify-between">
                  <span>Kullanılan Yedek Parçalar</span>
                  <span className="text-xs text-blue-600 font-semibold">{parts.length} Parça</span>
                </h3>

                {parts.length > 0 ? (
                  <div className="divide-y divide-slate-100 max-h-56 overflow-y-auto">
                    {parts.map(p => {
                      const linkedInv = p.inventoryItemId
                        ? inventory.find(i => i.id === p.inventoryItemId)
                        : inventory.find(i => i.name.trim().toLowerCase() === p.name.trim().toLowerCase());

                      const partCodeDisplay = p.code || linkedInv?.code || linkedInv?.id;

                      const oldQtyOnTicket = (ticket?.parts || [])
                        .filter(x => (linkedInv && x.inventoryItemId === linkedInv.id) || x.name.trim().toLowerCase() === p.name.trim().toLowerCase())
                        .reduce((sum, x) => sum + (x.quantity || 1), 0);

                      const otherPartsUsed = parts
                        .filter(x => x.id !== p.id && ((linkedInv && x.inventoryItemId === linkedInv.id) || x.name.trim().toLowerCase() === p.name.trim().toLowerCase()))
                        .reduce((sum, x) => sum + (x.quantity || 1), 0);

                      const maxAllowedForThisRow = linkedInv ? (linkedInv.quantity || 0) + oldQtyOnTicket - otherPartsUsed : 9999;
                      const isAtStockLimit = linkedInv ? p.quantity >= maxAllowedForThisRow : false;

                      return (
                        <div key={p.id} className="py-2.5 flex items-center justify-between text-xs gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {partCodeDisplay && (
                                <span className="font-mono text-[10px] font-bold bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded border border-slate-200 shrink-0" title="Parça / Stok Kodu">
                                  {partCodeDisplay}
                                </span>
                              )}
                              <span className="font-bold text-slate-800 truncate">{p.name}</span>
                              {p.inventoryItemId && (
                                <span className="inline-flex items-center gap-0.5 text-[10px] font-bold bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-200 shrink-0">
                                  <Package className="w-2.5 h-2.5" /> Stoklu
                                </span>
                              )}
                              {linkedInv && (
                                <span className="text-[10px] font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                                  Depo: {linkedInv.quantity} Adet
                                </span>
                              )}
                              {isContractedTicket && (
                                <label
                                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-[11px] select-none cursor-pointer transition-all shrink-0 ${
                                    p.isOutOfContract
                                      ? 'bg-purple-100 text-purple-950 border-purple-300 ring-1 ring-purple-400/40 shadow-2xs font-bold'
                                      : 'bg-emerald-50/90 hover:bg-emerald-100 text-emerald-900 border-emerald-200 font-semibold'
                                  } ${isLocked ? 'opacity-80 cursor-not-allowed' : ''}`}
                                  title={
                                    isLocked
                                      ? undefined
                                      : p.isOutOfContract
                                      ? 'Bu parça Sözleşme Dışı (Harici Tahsilat) olarak işaretli. Tıklayarak Sözleşme Kapsamına alabilirsiniz.'
                                      : 'Bu parça Sözleşme Kapsamında. Tıklayarak Sözleşme Dışı (Harici Tahsilat) yapabilirsiniz.'
                                  }
                                >
                                  <input
                                    type="checkbox"
                                    disabled={isLocked}
                                    checked={!!p.isOutOfContract}
                                    onChange={() => handleTogglePartOutOfContract(p.id)}
                                    className="w-3.5 h-3.5 text-purple-600 rounded border-purple-300 focus:ring-purple-500 cursor-pointer"
                                  />
                                  <span>
                                    {p.isOutOfContract ? (
                                      <span className="font-bold text-purple-950">⚡ Sözleşme Dışı (Harici)</span>
                                    ) : (
                                      <span className="font-medium text-emerald-900">✓ Sözleşme Kapsamında</span>
                                    )}
                                  </span>
                                </label>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-x-2 mt-1">
                              {/* Quantity Stepper */}
                              {canModifyDetails ? (
                                <div className="inline-flex items-center border border-slate-200 rounded-md bg-slate-50 overflow-hidden shadow-2xs">
                                  <button
                                    type="button"
                                    onClick={() => handleAdjustPartQty(p.id, -1)}
                                    className="px-1.5 py-0.5 text-slate-600 hover:bg-slate-200 font-bold transition-colors cursor-pointer"
                                    title="Azalt"
                                  >
                                    -
                                  </button>
                                  <span className="px-2 py-0.5 font-bold text-slate-800 bg-white min-w-[28px] text-center border-x border-slate-200">
                                    {p.quantity}
                                  </span>
                                  <button
                                    type="button"
                                    disabled={isAtStockLimit}
                                    onClick={() => handleAdjustPartQty(p.id, 1)}
                                    className={`px-1.5 py-0.5 font-bold transition-colors ${
                                      isAtStockLimit
                                        ? 'text-slate-300 bg-slate-100 cursor-not-allowed'
                                        : 'text-slate-600 hover:bg-slate-200 cursor-pointer'
                                    }`}
                                    title={isAtStockLimit ? `Stok sınırı (${maxAllowedForThisRow} adet) aşılamaz` : 'Artır'}
                                  >
                                    +
                                  </button>
                                </div>
                              ) : (
                                <span>Miktar: <b>{p.quantity} Adet</b></span>
                              )}
                              <span>•</span>
                              <span className="text-emerald-700 font-semibold">
                                Satış: {formatTL(p.unitPrice)}
                                {p.currency === 'USD' && p.unitPriceUSD !== undefined && p.unitPriceUSD > 0 && (
                                  <span className="text-blue-600 font-normal text-[10px] ml-1">({formatUSD(p.unitPriceUSD)})</span>
                                )}
                              </span>
                              <span>•</span>
                              <span className="text-amber-700 font-semibold">
                                Maliyet: {formatTL(p.costPrice || 0)}
                                {p.currency === 'USD' && p.costPriceUSD !== undefined && p.costPriceUSD > 0 && (
                                  <span className="text-amber-600 font-normal text-[10px] ml-1">({formatUSD(p.costPriceUSD)})</span>
                                )}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 shrink-0">
                            <div className="text-right">
                              <span className="font-bold text-slate-900 block">{formatTL(p.quantity * p.unitPrice)}</span>
                              {p.currency === 'USD' && p.unitPriceUSD !== undefined && p.unitPriceUSD > 0 && (
                                <span className="text-[10px] text-blue-600 font-medium block">
                                  {formatUSD(p.quantity * p.unitPriceUSD)}
                                </span>
                              )}
                              <span className="text-[10px] text-amber-600 font-medium block">Maliyet: {formatTL(p.quantity * (p.costPrice || 0))}</span>
                            </div>
                            {canModifyDetails && (
                              <button
                                type="button"
                                onClick={() => handleRemovePart(p.id)}
                                className="text-slate-400 hover:text-rose-600 p-1 rounded transition-colors cursor-pointer"
                                title="Sil"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic py-2 text-center">
                    Henüz yedek parça eklenmedi.
                  </p>
                )}

                {/* Part Error Message Alert */}
                {partErrorMsg && (
                  <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-lg flex items-start gap-2 text-xs text-rose-700 animate-in fade-in duration-200">
                    <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                    <div className="flex-1 font-semibold">{partErrorMsg}</div>
                    <button
                      type="button"
                      onClick={() => setPartErrorMsg('')}
                      className="text-rose-400 hover:text-rose-700 text-xs font-bold"
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* Add Part Form (Yetkili ise göster) */}
                {canModifyDetails && (
                  <div className="pt-2 border-t border-slate-100 space-y-2">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-bold text-slate-600 block">Yeni Parça Ekle:</span>
                        <div className="inline-flex items-center bg-slate-100 p-0.5 rounded border border-slate-200 text-[10px]">
                          <button
                            type="button"
                            onClick={() => setNewPartCurrency('TRY')}
                            className={`px-2 py-0.5 font-bold rounded cursor-pointer transition-colors ${
                              newPartCurrency === 'TRY' ? 'bg-white text-slate-900 shadow-2xs font-black' : 'text-slate-500 hover:text-slate-800'
                            }`}
                          >
                            ₺ TRY
                          </button>
                          <button
                            type="button"
                            onClick={() => setNewPartCurrency('USD')}
                            className={`px-2 py-0.5 font-bold rounded cursor-pointer transition-colors flex items-center gap-0.5 ${
                              newPartCurrency === 'USD' ? 'bg-blue-600 text-white shadow-2xs font-black' : 'text-slate-500 hover:text-slate-800'
                            }`}
                          >
                            <span>$ USD</span>
                          </button>
                        </div>
                        {newPartCurrency === 'USD' && (
                          <span className="text-[10px] text-blue-700 font-mono font-bold bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">
                            1 $ = {getStoredExchangeRate().rate.toFixed(2)} ₺
                          </span>
                        )}
                      </div>
                      {inventory.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setShowInventorySuggestions(!showInventorySuggestions)}
                          className="text-[11px] text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1 hover:underline cursor-pointer"
                        >
                          <Package className="w-3 h-3" />
                          <span>Stoktan Seç ({inventory.filter(i => i.quantity > 0).length})</span>
                        </button>
                      )}
                    </div>

                    {/* Active Selected Stock Info Banner */}
                    {selectedInvItem && (
                      <div
                        className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium border flex items-center justify-between flex-wrap gap-1 transition-all ${
                          isInvOutOfStock
                            ? 'bg-rose-50 border-rose-200 text-rose-800'
                            : isExceedingStock
                            ? 'bg-amber-50 border-amber-300 text-amber-900'
                            : 'bg-blue-50/80 border-blue-200 text-blue-900'
                        }`}
                      >
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Package className="w-3.5 h-3.5 shrink-0" />
                          <span>
                            {(selectedInvItem.code || selectedInvItem.id) && (
                              <span className="font-mono text-[10px] font-bold bg-blue-100 text-blue-900 px-1.5 py-0.5 rounded border border-blue-200 mr-1.5">
                                {selectedInvItem.code || selectedInvItem.id}
                              </span>
                            )}
                            <b className="text-slate-900">{selectedInvItem.name}</b> • Depo: <b>{selectedInvItem.quantity} Adet</b>
                            {alreadyAddedInParts > 0 && ` (Formda ekli: ${alreadyAddedInParts} adet)`}
                          </span>
                        </div>
                        <div>
                          {isInvOutOfStock ? (
                            <span className="font-bold text-rose-600">🚨 Stok Kalmadı (0 Adet)</span>
                          ) : isExceedingStock ? (
                            <span className="font-bold text-amber-700">
                              ⚠️ En fazla {remainingCanAdd} adet eklenebilir
                            </span>
                          ) : (
                            <span className="font-bold text-emerald-700">
                              ✓ {remainingCanAdd} adet eklenebilir
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-12 gap-1.5 items-center relative">
                      <div className="col-span-5 relative">
                        <input
                          type="text"
                          placeholder="Parça Kodu veya Adı ile Ara..."
                          value={newPartName}
                          onChange={e => {
                            setNewPartName(e.target.value);
                            setShowInventorySuggestions(true);
                            setPartErrorMsg('');
                            if (selectedInventoryId) setSelectedInventoryId('');
                          }}
                          onFocus={() => setShowInventorySuggestions(true)}
                          className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />

                        {/* Inventory Autocomplete Dropdown */}
                        {showInventorySuggestions && matchingInventoryItems.length > 0 && (
                          <div className="absolute left-0 top-full mt-1 w-88 max-w-[90vw] bg-white border border-slate-200 rounded-xl shadow-xl z-30 max-h-56 overflow-y-auto p-1 divide-y divide-slate-100">
                            <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center justify-between">
                              <span>Stoktaki Parçalar (Kod & Adı)</span>
                              <button
                                type="button"
                                onClick={() => setShowInventorySuggestions(false)}
                                className="text-slate-400 hover:text-slate-600"
                              >
                                ✕
                              </button>
                            </div>
                            {matchingInventoryItems.map(item => (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() => handleSelectInventoryItem(item)}
                                className="w-full text-left px-2.5 py-2 hover:bg-blue-50/80 rounded-lg transition-colors cursor-pointer block group"
                              >
                                <div className="flex items-start justify-between gap-1.5">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span className="font-mono text-[10px] font-bold bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded border border-slate-200 shrink-0">
                                        {item.code || item.id}
                                      </span>
                                      <span className="text-xs font-bold text-slate-800 group-hover:text-blue-700 truncate">
                                        {item.name}
                                      </span>
                                    </div>
                                    <div className="text-[10px] text-slate-500 flex items-center gap-1.5 mt-0.5">
                                      <span>{item.category || 'Genel'}</span>
                                      {[item.brand, item.model].filter(Boolean).length > 0 && (
                                        <span>• {[item.brand, item.model].filter(Boolean).join(' ')}</span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right shrink-0">
                                    <span
                                      className={`text-[10px] font-bold px-1.5 py-0.5 rounded inline-block ${
                                        item.quantity <= 0
                                          ? 'bg-rose-100 text-rose-700'
                                          : item.quantity <= (item.minQuantity || 2)
                                          ? 'bg-amber-100 text-amber-700'
                                          : 'bg-emerald-100 text-emerald-700'
                                      }`}
                                    >
                                      Stok: {item.quantity} Adet
                                    </span>
                                    <div className="text-[10px] text-slate-500 mt-0.5">
                                      Satış: <b className="text-emerald-600">{formatTL(item.salePrice)}</b>
                                    </div>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="col-span-2 relative">
                        <input
                          type="number"
                          min="1"
                          placeholder="Adet"
                          value={newPartQtyStr}
                          onChange={e => {
                            setNewPartQtyStr(e.target.value);
                            setPartErrorMsg('');
                          }}
                          className={`w-full px-1.5 py-1.5 text-xs border rounded-md text-center focus:outline-none focus:ring-1 font-bold ${
                            isExceedingStock || isInvOutOfStock
                              ? 'border-rose-400 bg-rose-50 text-rose-700 focus:ring-rose-500'
                              : 'border-slate-300 focus:ring-blue-500 text-slate-800'
                          }`}
                          title="Eklenecek Parça Adedi"
                        />
                      </div>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder={newPartCurrency === 'USD' ? 'Maliyet $' : 'Maliyet ₺'}
                        title={newPartCurrency === 'USD' ? 'Maliyet ($ Dolar) - TCMB kuruyla otomatik TL’ye çevrilir' : 'Maliyet (Alış Fiyatı) - Raporda gözükür, faturada gizlidir'}
                        value={newPartCost}
                        onChange={e => {
                          setNewPartCost(e.target.value);
                          setPartErrorMsg('');
                        }}
                        className="col-span-2 px-1.5 py-1.5 text-xs border border-amber-300 bg-amber-50/50 rounded-md text-right focus:outline-none focus:ring-1 focus:ring-amber-500 font-medium"
                      />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder={newPartCurrency === 'USD' ? 'Satış $' : 'Satış ₺'}
                        title={newPartCurrency === 'USD' ? 'Satış Fiyatı ($ Dolar) - Faturada resmi TL olarak gözükür' : 'Parça Satış Fiyatı - Müşteri Faturasında gözükür'}
                        value={newPartPrice}
                        onChange={e => {
                          setNewPartPrice(e.target.value);
                          setPartErrorMsg('');
                        }}
                        className="col-span-2 px-1.5 py-1.5 text-xs border border-blue-300 bg-blue-50/50 rounded-md text-right focus:outline-none focus:ring-1 focus:ring-blue-500 font-medium"
                      />
                      <button
                        type="button"
                        onClick={handleAddPart}
                        className={`col-span-1 text-white rounded-md h-8 flex items-center justify-center transition-colors cursor-pointer ${
                          isInvOutOfStock || isExceedingStock
                            ? 'bg-amber-600 hover:bg-amber-700'
                            : 'bg-blue-600 hover:bg-blue-500'
                        }`}
                        title="Ekle"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>

                    {/* USD Real-time TL preview indicator */}
                    {newPartCurrency === 'USD' && (parseFloat(newPartPrice) > 0 || parseFloat(newPartCost) > 0) && (
                      <div className="text-[10px] text-slate-500 bg-slate-50 p-1.5 rounded border border-slate-200 flex items-center justify-between">
                        <span>
                          Hesaplanan TL Değerleri (Kur: {getStoredExchangeRate().rate.toFixed(4)} ₺):
                        </span>
                        <div className="flex items-center gap-2 font-semibold">
                          {parseFloat(newPartCost) > 0 && (
                            <span className="text-amber-700">
                              Maliyet: {formatTL(convertUSDToTL(parseFloat(newPartCost), getStoredExchangeRate().rate))}
                            </span>
                          )}
                          {parseFloat(newPartPrice) > 0 && (
                            <span className="text-emerald-700">
                              Satış: {formatTL(convertUSDToTL(parseFloat(newPartPrice), getStoredExchangeRate().rate))}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Labor Cost, Tax, Discount & Total Calculation */}
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-3">
                <h3 className="font-bold text-sm text-slate-800 border-b pb-2 border-slate-100">
                  Faturalandırma & Ücret Hesabı
                </h3>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">
                      İşçilik (KDV Dahil) (₺)
                    </label>
                    <input
                      type="number"
                      min="0"
                      disabled={!canModifyDetails}
                      readOnly={!canModifyDetails}
                      placeholder="0"
                      value={laborCost || ''}
                      onChange={e => setLaborCost(Math.max(0, parseFloat(e.target.value) || 0))}
                      className={`w-full px-3 py-1.5 border rounded-lg text-sm font-semibold focus:outline-none ${
                        !canModifyDetails
                          ? 'bg-slate-100 text-slate-700 border-slate-200 cursor-not-allowed'
                          : 'bg-white text-slate-900 border-slate-300 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500'
                      }`}
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">
                      İskonto / İndirim (Ana Toplamdan) (₺)
                    </label>
                    <input
                      type="number"
                      min="0"
                      disabled={!canModifyDetails}
                      readOnly={!canModifyDetails}
                      placeholder="0"
                      value={discount || ''}
                      onChange={e => setDiscount(Math.max(0, parseFloat(e.target.value) || 0))}
                      className={`w-full px-3 py-1.5 border rounded-lg text-sm font-semibold text-rose-600 focus:outline-none ${
                        !canModifyDetails
                          ? 'bg-slate-100 text-rose-700 border-slate-200 cursor-not-allowed'
                          : 'bg-white border-slate-300 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500'
                      }`}
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">
                      Otomatik Yuvarlama (₺)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      disabled
                      readOnly
                      placeholder="Otomatik"
                      value={roundingAdjustment || ''}
                      className={`w-full px-3 py-1.5 border rounded-lg text-sm font-semibold focus:outline-none ${
                        !canModifyDetails
                          ? 'bg-slate-100 text-slate-700 border-slate-200 cursor-not-allowed'
                          : 'bg-white text-slate-900 border-slate-300 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500'
                      }`}
                    />
                    <div className="mt-1 text-[10px] text-slate-500">Genel toplam en yakın 10 TL’ye otomatik yuvarlanır; iskonto/indirim olarak raporlanmaz.</div>
                  </div>

                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">KDV Oranı (Parça +KDV / İşçilik KDV Dahil)</label>
                    <div
                      className="w-full px-3 py-1.5 border rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 border-slate-200 cursor-not-allowed"
                      title="KDV oranı sistem genelinde sabit %20'dir."
                    >
                      %20 KDV — Parça +KDV / İşçilik KDV Dahil
                    </div>
                  </div>

                  {/* Split Payment 3-Column Inputs (Hidden when contracted ticket has no out-of-contract parts) */}
                  {(!isContractedTicket || contractBreakdown.hasOutOfContractParts) && (
                    <div className="col-span-2">
                      <div className="bg-slate-50/90 p-3 rounded-xl border border-slate-200 space-y-2.5">
                        <div className="flex items-center justify-between pb-1 border-b border-slate-200">
                          <span className="text-[11px] font-bold text-slate-700 flex items-center gap-1">
                            <Wallet className="w-3.5 h-3.5 text-emerald-600" />
                            {isContractedTicket ? '⚡ Sözleşme Harici Tahsilat Dağılımı (Nakit / Kart / EFT)' : 'Ödeme Dağılımı (Nakit / Kart / EFT)'}
                          </span>
                          {remainingDebtAmount > 0 && !isLocked && (
                            <div className="flex items-center gap-1 text-[10px]">
                              <span className="text-slate-500">{isContractedTicket ? 'Kalan Harici Tutar:' : 'Kalan:'}</span>
                              <span className="font-bold text-rose-600 font-mono">{formatTL(remainingDebtAmount)}</span>
                            </div>
                          )}
                        </div>

                        {/* 3-Column Inputs */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                          {/* Cash Input */}
                          <div className="bg-white p-2.5 rounded-lg border border-slate-200 shadow-2xs">
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-[11px] font-bold text-emerald-800 flex items-center gap-1">
                                <span>💵 Nakit</span>
                              </label>
                              {remainingDebtAmount > 0 && !isLocked && (
                                <button
                                  type="button"
                                  onClick={() => setCashAmount(prev => Number((prev + remainingDebtAmount).toFixed(2)))}
                                  title="Kalan tutarı Nakit alanına ekle"
                                  className="text-[10px] text-emerald-700 hover:underline font-bold cursor-pointer"
                                >
                                  + Kalanı Doldur
                                </button>
                              )}
                            </div>
                            <div className="relative">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                disabled={isLocked}
                                value={cashAmount || ''}
                                onChange={e => setCashAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                                placeholder="0.00"
                                className={`w-full px-2.5 py-1.5 border rounded-lg text-sm font-bold text-slate-900 focus:outline-none ${
                                  isLocked
                                    ? 'bg-slate-100 border-slate-200 cursor-not-allowed text-slate-500'
                                    : 'bg-white border-slate-300 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500'
                                }`}
                              />
                              <span className="absolute right-2.5 top-1.5 text-xs text-slate-400 font-bold pointer-events-none">₺</span>
                            </div>
                          </div>

                          {/* Credit Card Input */}
                          <div className="bg-white p-2.5 rounded-lg border border-slate-200 shadow-2xs">
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-[11px] font-bold text-blue-800 flex items-center gap-1">
                                <span>💳 Kredi Kartı / POS</span>
                              </label>
                              {remainingDebtAmount > 0 && !isLocked && (
                                <button
                                  type="button"
                                  onClick={() => setCardAmount(prev => Number(Math.max(0, prev + remainingDebtAmount).toFixed(2)))}
                                  title="Kalan tutarı Kredi Kartı alanına ekle"
                                  className="text-[10px] text-blue-700 hover:underline font-bold cursor-pointer"
                                >
                                  + Kalanı Doldur
                                </button>
                              )}
                            </div>
                            <div className="relative">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                disabled={isLocked}
                                value={cardAmount || ''}
                                onChange={e => setCardAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                                placeholder="0.00"
                                className={`w-full px-2.5 py-1.5 border rounded-lg text-sm font-bold text-slate-900 focus:outline-none ${
                                  isLocked
                                    ? 'bg-slate-100 border-slate-200 cursor-not-allowed text-slate-500'
                                    : 'bg-white border-slate-300 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500'
                                }`}
                              />
                              <span className="absolute right-2.5 top-1.5 text-xs text-slate-400 font-bold pointer-events-none">₺</span>
                            </div>
                          </div>

                          {/* Transfer / EFT Input */}
                          <div className="bg-white p-2.5 rounded-lg border border-slate-200 shadow-2xs">
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-[11px] font-bold text-indigo-800 flex items-center gap-1">
                                <span>🏦 Havale / EFT</span>
                              </label>
                              {remainingDebtAmount > 0 && !isLocked && (
                                <button
                                  type="button"
                                  onClick={() => setTransferAmount(prev => Number(Math.max(0, prev + remainingDebtAmount).toFixed(2)))}
                                  title="Kalan tutarı Havale/EFT alanına ekle"
                                  className="text-[10px] text-indigo-700 hover:underline font-bold cursor-pointer"
                                >
                                  + Kalanı Doldur
                                </button>
                              )}
                            </div>
                            <div className="relative">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                disabled={isLocked}
                                value={transferAmount || ''}
                                onChange={e => setTransferAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                                placeholder="0.00"
                                className={`w-full px-2.5 py-1.5 border rounded-lg text-sm font-bold text-slate-900 focus:outline-none ${
                                  isLocked
                                    ? 'bg-slate-100 border-slate-200 cursor-not-allowed text-slate-500'
                                    : 'bg-white border-slate-300 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500'
                                }`}
                              />
                              <span className="absolute right-2.5 top-1.5 text-xs text-slate-400 font-bold pointer-events-none">₺</span>
                            </div>
                          </div>
                        </div>

                        {/* Live Balance Summary Bar */}
                        <div className="flex flex-wrap items-center justify-between text-xs pt-1.5 border-t border-slate-200/80 px-1">
                          <div className="flex items-center gap-3">
                            <span className="text-slate-600 font-semibold">
                              Toplam Alınan: <strong className="text-emerald-700 font-mono">{formatTL(totalPaid)}</strong>
                            </span>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              isContractedTicket
                                ? (!contractBreakdown.hasOutOfContractParts || totalPaid >= contractBreakdown.outOfContractTotal)
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : totalPaid > 0
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-slate-100 text-slate-600'
                                : (isUnderWarranty || ticket.serviceType === 'Garantili' || ticket.serviceType === 'ServisGarantili') && grandTotal <= 0
                                ? 'bg-emerald-100 text-emerald-800'
                                : totalPaid >= grandTotal && grandTotal > 0
                                ? 'bg-emerald-100 text-emerald-800'
                                : totalPaid > 0
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {isContractedTicket
                                ? (!contractBreakdown.hasOutOfContractParts
                                    ? 'Sözleşme Kapsamında (Ödendi)'
                                    : totalPaid >= contractBreakdown.outOfContractTotal
                                    ? 'Harici Tutar Ödendi'
                                    : totalPaid > 0
                                    ? 'Kısmi Harici Ödeme'
                                    : 'Harici Ödeme Bekliyor')
                                : totalPaid >= grandTotal && grandTotal > 0
                                ? 'Tam Tahsil Edildi'
                                : (isUnderWarranty || ticket.serviceType === 'Garantili' || ticket.serviceType === 'ServisGarantili') && grandTotal <= 0
                                ? 'Garanti Kapsamında (Ücret Yok)'
                                : totalPaid > 0
                                ? 'Ara Ödeme / Kısmi'
                                : 'Ödenmedi'}
                            </span>
                          </div>

                          <div>
                            <span className="text-slate-600 font-semibold">
                              {isContractedTicket ? 'Kalan Harici Borç: ' : 'Kalan Borç: '}
                              <strong className={`font-mono ${remainingDebtAmount > 0 ? 'text-rose-600 font-bold' : 'text-emerald-600 font-bold'}`}>
                                {formatTL(remainingDebtAmount)}
                              </strong>
                            </span>
                          </div>
                        </div>

                        {/* Payment Records List with Real Timestamps */}
                        {ticket.payments && ticket.payments.length > 0 && (
                          <div className="pt-2 border-t border-slate-200/80 space-y-1.5">
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">
                              Geçmiş Ödeme / Tahsilat Hareketleri ({ticket.payments.length}):
                            </span>
                            <div className="max-h-32 overflow-y-auto space-y-1 divide-y divide-slate-100 bg-white p-2 rounded-lg border border-slate-200">
                              {ticket.payments.map((p) => {
                                const methodNorm = normalizePaymentMethod(p.method);
                                return (
                                  <div key={p.id} className="pt-1 first:pt-0 flex items-center justify-between text-xs">
                                    <div className="flex items-center gap-2">
                                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                                        methodNorm === 'Nakit'
                                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                          : methodNorm === 'Kredi Karti'
                                          ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                          : 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                                      }`}>
                                        {methodNorm === 'Nakit' ? '💵 Nakit' : methodNorm === 'Kredi Karti' ? '💳 Kart' : '🏦 Havale'}
                                      </span>
                                      <div>
                                        <span className="font-semibold text-slate-800">{p.note || p.method}</span>
                                        <span className="text-[10px] text-slate-400 ml-1.5">{formatDateTR(p.date)}</span>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="font-bold text-emerald-700 font-mono">+{formatTL(p.amount)}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Calculation Breakdown */}
                <div className="bg-slate-900 text-white p-4 rounded-xl mt-3 space-y-2">
                  {isContractedTicket && (
                    <div className="pb-2 border-b border-slate-800 space-y-1">
                      <div className="flex justify-between text-xs text-purple-300 font-semibold">
                        <span>Sözleşme Kapsamındaki Tutar (Mahsup):</span>
                        <span className="font-mono">{formatTL(contractBreakdown.inContractGrandTotal)}</span>
                      </div>
                      {contractBreakdown.hasOutOfContractParts && (
                        <div className="flex justify-between text-xs text-purple-200 font-bold">
                          <span>⚡ Sözleşme Harici Parça Tutarı (Harici):</span>
                          <span className="font-mono text-emerald-400">{formatTL(contractBreakdown.outOfContractTotal)}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex justify-between text-xs text-slate-300">
                    <span>Yedek Parça Satış Toplamı:</span>
                    <span className="font-mono">{formatTL(partsTotal)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-amber-300/90 font-medium">
                    <span>Parça Maliyeti (Gider):</span>
                    <span className="font-mono">{formatTL(partsCostTotal)}</span>
                  </div>
                  {subServiceCost > 0 && (
                    <div className="flex justify-between text-xs text-cyan-300/90 font-medium">
                      <span>Alt Servis Maliyeti (Gider):</span>
                      <span className="font-mono">{formatTL(subServiceCost)}</span>
                    </div>
                  )}
                  {(partsCostTotal > 0 || subServiceCost > 0) && (
                    <div className="flex justify-between text-xs text-rose-300/90 font-semibold border-t border-slate-800 pt-1">
                      <span>Toplam Servis Maliyeti (Gider):</span>
                      <span className="font-mono">-{formatTL(partsCostTotal + (subServiceCost || 0))}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs text-slate-300">
                    <span>İşçilik Tutarı:</span>
                    <span className="font-mono">{formatTL(laborCost)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-xs text-rose-400">
                      <span>İndirim / İskonto:</span>
                      <span className="font-mono">-{formatTL(discount)}</span>
                    </div>
                  )}
                  {roundingAdjustment !== 0 && (
                    <div className="flex justify-between text-xs text-sky-300">
                      <span>Yuvarlama:</span>
                      <span className="font-mono">{roundingAdjustment > 0 ? '+' : ''}{formatTL(roundingAdjustment)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs text-slate-300">
                    <span>
                      Hesaplanan KDV (%{vatRate}) (Parça +KDV / İşçilik Dahil):
                    </span>
                    <span className="font-mono">{formatTL(vatAmount)}</span>
                  </div>
                  {vatRate > 0 && (
                    <div className="flex justify-between text-[11px] text-slate-400">
                      <span>KDV Hariç Net Matrah:</span>
                      <span className="font-mono">{formatTL(subtotal)}</span>
                    </div>
                  )}
                  <div className="border-t border-slate-700 pt-2 flex justify-between items-center">
                    <span className="font-bold text-sm text-white">
                      GENEL TOPLAM:
                    </span>
                    <span className="font-mono text-lg font-bold text-emerald-400">
                      {formatTL(grandTotal)}
                    </span>
                  </div>

                  {/* Net Profit Calculation */}
                  <div className="pt-2 border-t border-slate-800 flex justify-between items-center text-xs">
                    <span className="text-slate-400 font-medium">
                      {isContractedTicket ? 'Net Tahmini Kâr (Ekstra Parça / Matrah - Maliyet):' : 'Net Tahmini Kâr (Matrah - Maliyet):'}
                    </span>
                    <span className={`font-mono font-bold ${subtotal - (partsCostTotal + (subServiceCost || 0)) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {formatTL(subtotal - (partsCostTotal + (subServiceCost || 0)))}
                    </span>
                  </div>

                  {isContractedTicket ? (
                    <div className="pt-2 border-t border-slate-800 space-y-1">
                      {contractBreakdown.hasOutOfContractParts ? (
                        <>
                          <div className="flex justify-between text-xs text-emerald-300 font-semibold">
                            <span>Alınan Harici Ödeme:</span>
                            <span className="font-mono">{formatTL(totalPaid)}</span>
                          </div>
                          <div className="flex justify-between text-xs text-rose-300 font-bold">
                            <span>Kalan Harici Borç (Tahsil Edilecek):</span>
                            <span className="font-mono">{formatTL(remainingDebtAmount)}</span>
                          </div>
                        </>
                      ) : (
                        <div className="text-xs text-emerald-400 font-semibold flex items-center justify-between">
                          <span>Sözleşmeli Cari Bakiye:</span>
                          <span>✓ Cari Borç Yok (Sözleşme Kapsamı)</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    paymentStatus === 'KismiOdendi' && (
                      <div className="pt-2 border-t border-slate-800 space-y-1">
                        <div className="flex justify-between text-xs text-emerald-300 font-semibold">
                          <span>Alınan Ödeme (Kısmi):</span>
                          <span className="font-mono">{formatTL(totalPaid)}</span>
                        </div>
                        <div className="flex justify-between text-xs text-rose-300 font-bold">
                          <span>Kalan Borç (Müşteri Bakiye):</span>
                          <span className="font-mono">{formatTL(Math.max(0, grandTotal - totalPaid))}</span>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Bottom Right: Dedicated Technician Notes & Partial Payments Area */}
              <div className="bg-slate-50/90 p-4 rounded-xl border border-amber-300/80 shadow-sm space-y-3.5 mt-4">
                <div className="flex items-center justify-between border-b pb-2 border-amber-200/80">
                  <h4 className="text-xs font-bold text-amber-950 uppercase tracking-wider flex items-center gap-1.5">
                    <Wrench className="w-4 h-4 text-amber-600" />
                    Tekniker Notu & Ara Ödemeler
                  </h4>
                </div>

                {/* Technician Internal Note Textarea */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-bold text-slate-700">
                      Tekniker Notu
                    </label>
                    {isLimitedTechnician && (
                      <span className="text-[10px] text-slate-500 font-medium bg-slate-200/80 px-2 py-0.5 rounded">
                        Sadece Görüntüleme
                      </span>
                    )}
                  </div>
                  <textarea
                    rows={3}
                    disabled={isLocked || isLimitedTechnician}
                    readOnly={isLocked || isLimitedTechnician}
                    value={technicianNotes}
                    onChange={e => setTechnicianNotes(e.target.value)}
                    placeholder={
                      isLimitedTechnician
                        ? 'Karşılama: Tekniker notunu sadece görebilirsiniz.'
                        : isLocked
                        ? 'Kayıt kilitli.'
                        : 'Tekniker notu...'
                    }
                    className={`w-full text-xs p-2.5 border rounded-lg focus:outline-none ${
                      isLocked || isLimitedTechnician
                        ? 'bg-slate-100 text-slate-700 border-slate-200 cursor-not-allowed select-text'
                        : 'bg-white text-slate-900 border-amber-300 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500'
                    }`}
                  />
                </div>

                {/* Live Collected Payments & Partial Payment Details */}
                <div className="bg-white p-3 rounded-lg border border-slate-200/90 space-y-2 text-xs">
                  <div className="flex items-center justify-between border-b pb-1.5 border-slate-100">
                    <span className="font-bold text-slate-800 flex items-center gap-1.5">
                      <Wallet className="w-3.5 h-3.5 text-emerald-600" />
                      Alınan Ara Ödemeler & Tahsilat Bilgisi
                    </span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                      totalPaid >= grandTotal && grandTotal > 0
                        ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                        : totalPaid > 0
                        ? 'bg-amber-100 text-amber-800 border border-amber-200'
                        : 'bg-slate-100 text-slate-600 border border-slate-200'
                    }`}>
                      {totalPaid >= grandTotal && grandTotal > 0
                        ? 'Tam Tahsil Edildi'
                        : totalPaid > 0
                        ? 'Ara Ödeme Alındı'
                        : 'Ödeme Alınmadı'}
                    </span>
                  </div>

                  {/* Payment Breakdown Cards */}
                  <div className="grid grid-cols-3 gap-2 text-center pt-1">
                    <div className="bg-emerald-50/70 p-1.5 rounded border border-emerald-100">
                      <span className="text-[10px] text-emerald-700 font-semibold block">💵 Nakit</span>
                      <span className="text-xs font-bold text-emerald-900 font-mono">{formatTL(cashAmount)}</span>
                    </div>
                    <div className="bg-blue-50/70 p-1.5 rounded border border-blue-100">
                      <span className="text-[10px] text-blue-700 font-semibold block">💳 Kredi Kartı</span>
                      <span className="text-xs font-bold text-blue-900 font-mono">{formatTL(cardAmount)}</span>
                    </div>
                    <div className="bg-indigo-50/70 p-1.5 rounded border border-indigo-100">
                      <span className="text-[10px] text-indigo-700 font-semibold block">🏦 Havale/EFT</span>
                      <span className="text-xs font-bold text-indigo-900 font-mono">{formatTL(transferAmount)}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-1 border-t border-slate-100 text-[11px]">
                    <span className="text-slate-600">
                      Toplam Alınan: <strong className="text-emerald-700 font-mono">{formatTL(totalPaid)}</strong>
                    </span>
                    <span className="text-slate-600">
                      Kalan Bakiye: <strong className={`font-mono ${Math.max(0, grandTotal - totalPaid) > 0 ? 'text-rose-600 font-bold' : 'text-emerald-700 font-bold'}`}>
                        {formatTL(Math.max(0, grandTotal - totalPaid))}
                      </strong>
                    </span>
                  </div>

                  {/* Payment Records list if available */}
                  {ticket.payments && ticket.payments.length > 0 && (
                    <div className="pt-2 border-t border-slate-100 space-y-1">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Ödeme Hareket Geçmişi:</span>
                      <div className="max-h-28 overflow-y-auto space-y-1 divide-y divide-slate-100">
                        {ticket.payments.map((p) => {
                          const methodNorm = normalizePaymentMethod(p.method);
                          return (
                            <div key={p.id} className="pt-1 first:pt-0 flex items-center justify-between text-[11px]">
                              <div>
                                <span className="font-semibold text-slate-700">{p.note || p.method}</span>
                                <span className="text-[10px] text-slate-400 ml-1.5">{formatDateTR(p.date)}</span>
                              </div>
                              <span className="font-bold text-emerald-700 font-mono">+{formatTL(p.amount)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="bg-slate-100 px-4 sm:px-6 py-3 border-t border-slate-200 flex flex-wrap items-center justify-between gap-2">
          {isAdmin && !ticket.isDeleted ? (
            <button
              type="button"
              disabled={isLocked}
              onClick={handleDelete}
              className={`px-3 py-2 border rounded-lg text-xs font-bold flex items-center space-x-1.5 transition-colors ${
                isLocked
                  ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-60'
                  : 'bg-rose-50 hover:bg-rose-100 text-rose-700 border-rose-200 cursor-pointer'
              }`}
              title={isLocked ? 'Kilitli servis kaydı gizlenemez' : 'Kaydı Sistemden Gizle'}
            >
              <Trash2 className="w-4 h-4" />
              <span>Kaydı Gizle</span>
            </button>
          ) : (
            <div />
          )}

          <div className="flex items-center space-x-3">
            {savedToast && (
              <span className="text-xs font-bold text-emerald-600 flex items-center gap-1">
                <Check className="w-4 h-4" />
                <span>Kaydedildi</span>
              </span>
            )}

            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-200 text-sm font-semibold transition-colors cursor-pointer"
            >
              Kapat
            </button>

            {isLocked ? (
              <div className="px-5 py-2 bg-slate-200 text-slate-700 rounded-lg text-sm font-bold border border-slate-300 flex items-center space-x-2 select-none shadow-xs">
                <Lock className="w-4 h-4 text-slate-500" />
                <span>Kayıt Kilitli (Düzenlenemez)</span>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleSave}
                className={`px-6 py-2 text-white rounded-lg text-sm font-bold shadow-md flex items-center space-x-2 transition-all hover:scale-[1.02] cursor-pointer ${
                  savedToast
                    ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/30 ring-2 ring-emerald-400'
                    : 'bg-blue-600 hover:bg-blue-500 shadow-blue-600/30'
                }`}
              >
                <Check className="w-4 h-4" />
                <span>{savedToast ? 'Kaydedildi ✓' : 'Değişiklikleri Kaydet'}</span>
              </button>
            )}
          </div>
        </div>

        {/* Pattern Lock Modal */}
        <PatternLockModal
          isOpen={isPatternModalOpen}
          initialPattern={devicePassword}
          onClose={() => setIsPatternModalOpen(false)}
          onSave={(patternStr) => setDevicePassword(patternStr)}
        />
      </div>
      </div>
</div>
  );
};
